import React, { useState, useEffect, useRef } from 'react';
import { Tree, Button, Spin, Radio, Form, Progress, Card, Descriptions, List, Statistic, Modal, Popconfirm, Layout, Alert, Upload, message, Space, Input, Table, Row, Col, Badge, Tooltip, Select } from 'antd';
import axios from 'axios';
import { CloudUploadOutlined, FileZipOutlined, UploadOutlined, FileTextOutlined, LoadingOutlined, PaperClipOutlined, FolderOpenOutlined, InboxOutlined, SearchOutlined, FolderAddFilled, EyeOutlined, FileExclamationOutlined, CloseOutlined, FileOutlined, EditOutlined, SaveOutlined, FolderOutlined, DownloadOutlined, DeleteOutlined, WarningOutlined, QuestionCircleOutlined, ReloadOutlined, ApartmentOutlined } from '@ant-design/icons';
import { cloneDeep } from 'lodash';
import jsPDF from 'jspdf';
import { checkFolderName, createFolder } from '../../services/project/checkFolder';
import config from '../../config';
import { useSelector } from 'react-redux';
import { selectAppPermissions } from '../../store/auth/selectors';
import { logAction } from '../Poa/logActionsPOA';
import { fetchPoaDataByPath } from '../../services/poa/get_poaDocumentosMedios';
const { Content, Sider } = Layout;
const { Dragger } = Upload;
const { DirectoryTree } = Tree;
const baseUrl = config.baseUrl;

const truncateText = (text, maxLength) => {
    if (!text || typeof text !== 'string') {
        return '';
    }
    if (text.length <= maxLength) {
        return text;
    }
    return text.substring(0, maxLength) + '...';
};

const cleanFileName = (encodedFileName) => {
    const decodedFileName = decodeURIComponent(encodedFileName);
    const match = decodedFileName.match(/^(\d+)[_-](.+)$/);
    return match ? match[2] : decodedFileName;
};

const truncateTextName = (text, maxLength) => {
    if (!text || typeof text !== 'string') {
        return '';
    }
    if (text.length <= maxLength) {
        return text;
    }
    return text.substring(0, maxLength) + '...';
};

const processFileName = (fileName, maxLength) => {
    const cleanedFileName = cleanFileName(fileName);
    return truncateTextName(cleanedFileName, maxLength);
};

// Rutas donde se habilita el filtro de coincidencias (soportes con el mismo
// nombre/cédula repetidos en una misma carpeta): Actas y Visitas de acompañamiento.
const RUTAS_FILTRO_COINCIDENCIAS = new Set([
    'tarea_214/indicador_30/medio_verificacion_25',
    'tarea_221/indicador_34/medio_verificacion_29',
]);

/**
 * Extrae la cédula/identificador desde el nombre de un soporte para detectar
 * coincidencias (mismo criterio que el backend: prefijo "C" opcional, últimos
 * 6-12 dígitos, descartando fechas ddmmyyyy).
 */
const extraerIdentificadorSoporte = (nombreArchivo) => {
    if (!nombreArchivo) return null;
    let limpio;
    try {
        limpio = decodeURIComponent(nombreArchivo);
    } catch {
        limpio = nombreArchivo;
    }
    limpio = limpio
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/^\d{10,}[-_]/, '')
        .replace(/(\.[a-z0-9]+)+$/gi, '')
        .replace(/\s*\([^)]*\)\s*/g, ' ')
        .replace(/(?:^|[\s_\-.])C(\d+)/gi, '$1')
        .replace(/[._-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const matches = Array.from(limpio.matchAll(/\d{6,12}/g)).map((m) => m[0]);
    if (!matches.length) return null;

    const validos = matches.filter((num) => {
        if (num.length !== 8) return true;
        const dd = Number(num.slice(0, 2));
        const mm = Number(num.slice(2, 4));
        const yyyy = Number(num.slice(4, 8));
        return !(dd >= 1 && dd <= 31 && mm >= 1 && mm <= 12 && yyyy >= 1900 && yyyy <= 2100);
    });
    if (!validos.length) return null;

    return validos[validos.length - 1];
};

/**
 * Agrupa los soportes de una carpeta por su identificador y, según el filtro
 * de coincidencias, deja solo los repetidos o solo los únicos, reordenándolos
 * para que los que comparten identificador queden uno debajo del otro.
 * Las carpetas nunca se filtran, solo los archivos.
 */
const aplicarFiltroCoincidencias = (items, filtro) => {
    if (!Array.isArray(items) || filtro === 'todos') return items;

    const carpetas = items.filter((i) => i.type === 'folder');
    const archivos = items.filter((i) => i.type !== 'folder');

    const grupos = new Map();
    archivos.forEach((archivo) => {
        const identificador = extraerIdentificadorSoporte(archivo.viewName || archivo.name) ?? `__sin_identificar_${archivo.key}`;
        if (!grupos.has(identificador)) grupos.set(identificador, []);
        grupos.get(identificador).push(archivo);
    });

    const resultado = [];
    grupos.forEach((grupo) => {
        const esRepetido = grupo.length > 1;
        if ((filtro === 'repetidos' && esRepetido) || (filtro === 'unicos' && !esRepetido)) {
            resultado.push(...grupo);
        }
    });

    return [...carpetas, ...resultado];
};

/** Indica si una carpeta del árbol pertenece a alguna de las rutas con filtro de coincidencias */
const rutaConFiltroCoincidencias = (path) =>
    Array.from(RUTAS_FILTRO_COINCIDENCIAS).some((r) => (path || '').includes(r));

/**
 * Recorre todo el árbol y, en cada carpeta que pertenezca a una ruta con
 * filtro de coincidencias habilitado, aplica el filtro sobre sus soportes.
 * El resto del árbol se deja intacto.
 */
const aplicarFiltroCoincidenciasEnArbol = (nodes, filtro) => {
    if (!Array.isArray(nodes) || filtro === 'todos') return nodes;
    return nodes.map((node) => {
        if (node.type !== 'folder' || !Array.isArray(node.children)) return node;
        const hijosRecursivos = aplicarFiltroCoincidenciasEnArbol(node.children, filtro);
        const hijosFinal = rutaConFiltroCoincidencias(node.path)
            ? aplicarFiltroCoincidencias(hijosRecursivos, filtro)
            : hijosRecursivos;
        return { ...node, children: hijosFinal };
    });
};

const FileManagerPP = ({ codigo_tarea, select_proyect, esInformeFinal = false }) => {
    const [treeData, setTreeData] = useState([]);
    const [, settreeFilesData] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const user = useSelector((state) => state.auth.user);
    const userLogin = user.username;
    const [versionFrom, setVersionFrom] = useState([null]);
    const tmpCodigo = String(quitarPuntos(codigo_tarea.codTarea)).replace(/\./g, '');
    const codigo_proyecto_busqueda = 'tarea_' + tmpCodigo;
    const rutaActualIndicadorMV = `${codigo_proyecto_busqueda}/indicador_${codigo_tarea.codIndicador}/medio_verificacion_${codigo_tarea.codMV}`;
    const mostrarFiltroCoincidencias = RUTAS_FILTRO_COINCIDENCIAS.has(rutaActualIndicadorMV);
    const [filtroCoincidencias, setFiltroCoincidencias] = useState('todos');
    const [loading, setLoading] = useState(true);
    const [expandAll,] = useState(true);
    const [, settituloNameProyecto] = useState('');
    const nameproyecto = "Tarea POA";
    const [form] = Form.useForm();
    const [showWarning, setShowWarning] = useState(false);
    const [rootNodeSelected, setRootNodeSelected] = useState(true);
    const [activeButton, setActiveButton] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [newName, setNewName] = useState(selectedItem ? selectedItem.name : '');
    const [, setOriginalName] = useState(selectedItem ? selectedItem.name : "");
    const [, setProyectos] = useState([]);
    const [, setSearchValueCod] = useState('');
    const [isModalFileVisible, setIsModalFileVisible] = useState(false);
    const [uploadProgress,] = useState(0);
    const [fileToUpload, setFileToUpload] = useState(null);
    const [, setFileList] = useState([]);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [autoExpandParent, setAutoExpandParent] = useState(true);
    const inputRef = useRef(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [dataFile, setDataFile] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Módulo de subida múltiple
    const [modalMultipleVisible, setModalMultipleVisible] = useState(false);
    const [multipleFileList, setMultipleFileList] = useState([]);
    const [uploadingMultiple, setUploadingMultiple] = useState(false);
    const [uploadProgresAll, setUploadProgresAll] = useState(0);
    const [loadingFiles, setLoadingFiles] = useState(false);

    //Control de Permisos
    const appName = 'file-managerPOA';
    const permissions = useSelector((state) => selectAppPermissions(state, appName));
    const permissionDelete = permissions.priv_delete === 'Y';
    const permissionUpdate = permissions.priv_update === 'Y';
    const permissionInsert = permissions.priv_insert === 'Y';
    const permissionExport = permissions.priv_export === 'Y';
    const permissionPrint = permissions.priv_print === 'Y';

    // Exportar árbol de carpetas/archivos: exclusivo de Informe Técnico Final.
    // El permiso se resuelve aquí (app 'informe_tecnico_final') para que sea
    // consistente sin importar por cuál pantalla se llegue a este componente;
    // `esInformeFinal` acota el contexto para que no se filtre a la navegación
    // genérica de tareas del POA.
    const permissionsInformeTecnico = useSelector((state) => selectAppPermissions(state, 'informe_tecnico_final'));
    const habilitarExportarArbol = esInformeFinal && permissionsInformeTecnico.priv_export === 'Y';

    // Control de permisos para el filtro de coincidencias
    const permission8 = useSelector((state) => selectAppPermissions(state, 'file-managerPOA-Filter'));
    const permissionAccess8 = permission8.priv_access === 'Y';

    const onExpand = (newExpandedKeys) => {
        setExpandedKeys(newExpandedKeys);
        setAutoExpandParent(true);
    };

    const showCreateFolderModal = () => {
        setIsModalVisible(true);
    };

    useEffect(() => {
        setDataFile(null);
        setTreeData([]);
        setExpandedKeys([]);
        setLoading(true);
        setSelectedItem(null);
        setRootNodeSelected(true);
    }, [select_proyect]);

    useEffect(() => {
        if (isModalVisible && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isModalVisible]);

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            handleCreateFolder();
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!codigo_proyecto_busqueda) {
                setLoading(false);
                setShowWarning(true);
                settituloNameProyecto("FAO - UTF COL 160 COL");
                return;
            }

            settituloNameProyecto(`${codigo_proyecto_busqueda} ${nameproyecto}`);
            try {
                const pp = codigo_tarea.proyecto;
                const codigActividad = 'actividad_' + quitarPuntos(codigo_tarea.codActividad);
                const tarea = 'tarea_' + quitarPuntos(codigo_tarea.codTarea);
                const indicad = 'indicador_' + codigo_tarea.codIndicador;
                const mvcod = 'medio_verificacion_' + codigo_tarea.codMV;
                const response = await axios.get(`${baseUrl}/api/directorytreepoaPP?proyecto=${pp}&codigActividad=${codigActividad}&codigoTarea=${tarea}&codigoIndicador=${indicad}&mvcod=${mvcod}`);
                if (!response.data || response.data.length === 0) {
                    message.error("La ruta especificada no existe o está vacía.");
                    setTreeData([]);
                    return;
                }
                const data = response.data;
                const indicador = codigo_tarea.codIndicador;
                const fullPath = "DocumentosPOA/PlanOperativo/" + codigActividad + "/" + codigo_proyecto_busqueda + "/" + indicador + "/";
                const documents = await fetchPoaDataByPath(fullPath);
                setDataFile(documents);
                setVersionFrom(quitarPuntos(codigo_tarea.codActividad));
                const sortedData = data.map(sortChildren);
                setTreeData(sortedData);
                setSelectedItem(sortedData[0]);
                setTableParams(prevParams => ({
                    ...prevParams,
                    pagination: {
                        ...prevParams.pagination,
                    },
                }));
                setExpandedKeys(keys => {
                    if (!keys.includes('0')) {
                        return [...keys, '0'];
                    }
                    return keys;
                });

                if (!expandAll && data.length > 0) {
                    const initialKeysToExpand = data.slice(0, 9).map(item => item.key);
                    setExpandedKeys(initialKeysToExpand);
                }
            } catch (error) {
                console.error('Error fetching file tree:', error);
                message.error("Error al cargar los datos. Por favor, verifica la conexión o los parámetros.");
                setTreeData([]); // Limpiar datos
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [select_proyect, codigo_tarea.proyecto, codigo_tarea.codActividad, codigo_tarea.codTarea, codigo_tarea.codIndicador, codigo_tarea.codMV]);

    const sortChildren = (node) => {
        if (!node.children || node.children.length === 0) {
            return node;
        }
        node.children.sort((a, b) => {
            if (a.type !== b.type) {
                return a.type === "folder" ? -1 : 1;
            }
            const extractLeadingNumber = (name) => {
                const match = name.match(/^(\d+)/);
                return match ? parseInt(match[0], 10) : null;
            };
            const numA = extractLeadingNumber(a.name);
            const numB = extractLeadingNumber(b.name);
            if (numA !== null && numB !== null) {
                return numA - numB;
            }
            if (numA !== null) return -1;
            if (numB !== null) return 1;
            return a.name.localeCompare(b.name, undefined, { numeric: true });
        });

        node.children = node.children.map(sortChildren);
        return node;
    };

    function quitarPuntos(str) {
        return str.replace(/\./g, '');
    }

    useEffect(() => {
        const fetchProyectos = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${baseUrl}/api/arbolProyectos`);
                const data = response.data.data;
                setProyectos(data);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching proyectos:', error);
                setLoading(false);
            }
        };

        fetchProyectos();
    }, [setLoading]);

    const handleOkFile = () => {
        if (selectedItem && selectedItem.pathFromClient) {
        } else {
            console.error('selectedItem o pathFromClient es nulo o indefinido.');
        }
        setIsModalFileVisible(true);
        form.resetFields();
    };

    const handleCancelFile = () => {
        form.resetFields();
        setIsModalFileVisible(false);
    };

    const [hover, setHover] = useState(false);

    const handleCustomRequest = async ({ file, onSuccess, onError, resourceType }) => {
        try {

            if (!file) {
                return onError(new Error('Nombre del soporte es requerido'));
            }
            const pp = codigo_tarea.proyecto;
            const codigActividad = '/actividad_' + quitarPuntos(codigo_tarea.codActividad);
            const tarea = '/tarea_' + quitarPuntos(codigo_tarea.codTarea);
            const indicad = '/indicador_' + codigo_tarea.codIndicador;
            const mvcod = '/medio_verificacion_' + codigo_tarea.codMV;
            const nodeRaiz = 'DocumentosPOA/PlanOperativo';
            const codActividadParametro = pp + codigActividad + tarea + indicad + mvcod;
            const tmpRuta = nodeRaiz + '/' + codActividadParametro + '/' + selectedItem.pathFromClient;
            const resourceInfo = await handleAction({
                user: userLogin || "admin",
                actionType: 'uploadFile',
                nameServer: file.name,
                resourceDetails: {
                    nameResource: file.name,
                    Path: `${codActividadParametro}/${selectedItem.pathFromClient}`,
                    fatherResource: selectedItem.name,
                    typeResource: 'file',
                    key: `${selectedItem.pathFromClient}/${file.name}`,
                    fullPath: `${tmpRuta}/${file.name}`,
                    resourceType: resourceType
                }
            });
            const formData = new FormData();
            formData.append('pathFromClient', tmpRuta);
            formData.append('file', file);
            formData.append('resourceInfo', JSON.stringify(resourceInfo));
            const response = await axios.post(`${baseUrl}/api/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            const data = response.data;
            if (response.status === 200 && data.status === 'success') {
                message.success(`${file.name} Archivo cargado.`);
                // Actualiza el árbol de directorios con el nuevo archivo
                const uid = generateRandomUid();
                const newKey = `${selectedItem.key} - ${uid}`;
                const newFileNode = {
                    name: file.name,
                    key: newKey,
                    type: 'file',
                    level: selectedItem.level + 1,
                    path: `${selectedItem.pathFromClient}/${data.serverFileName}`, // Usa las comillas invertidas correctas
                    pathFromClient: selectedItem.pathFromClient,
                    uid: uid,
                    serverFileName: data.serverFileName,
                    isLeaf: true,
                    viewName: data.viewName,
                    typeS: 'Soporte',
                    resourceType: resourceType
                };
                const pp = codigo_tarea.proyecto;
                const codigActividad = '/actividad_' + quitarPuntos(codigo_tarea.codActividad);
                const tarea = '/tarea_' + quitarPuntos(codigo_tarea.codTarea);
                const indicad = '/indicador_' + codigo_tarea.codIndicador;
                const mvcod = '/medio_verificacion_' + codigo_tarea.codMV;
                const nodeRaiz = 'DocumentosPOA/PlanOperativo';
                const codActividadParametro = pp + codigActividad + tarea + indicad + mvcod;
                const now = new Date(); // Fecha y hora actual
                const isoString = now.toISOString(); // Convierte a formato ISO 8601
                //Actualizamos los datos del FileData
                const newFileNodeDataFile =
                {
                    nameResource: file.name,
                    fullPath: `${nodeRaiz}/${codActividadParametro}/${selectedItem.pathFromClient}/${data.serverFileName}`,
                    user: userLogin,
                    state: 1,
                    typeAction: "uploadFile",
                    dateTime: `${isoString}`,
                    detailJSON: JSON.stringify(resourceInfo)
                }
                setTreeData((prevTreeData) => addFileToTree(prevTreeData, selectedItem.pathFromClient, newFileNode));
                setDataFile((prevDataFile) => [...prevDataFile, newFileNodeDataFile]);
                // Encuentra el nodo actualizado
                const updatedNode = findNode(treeData, selectedItem.pathFromClient);
                if (updatedNode) {
                    // Actualiza el estado de selectedItem para reflejar los cambios en el árbol
                    setSelectedItem(prevSelectedItem => {
                        // Si la carpeta seleccionada es la misma que el nodo actualizado, actualiza sus hijos
                        if (prevSelectedItem.path === updatedNode.path) {
                            const updatedChildren = [...updatedNode.children, {
                                name: file.name,
                                key: newKey,
                                type: 'file',
                                level: selectedItem.level + 1,
                                path: `${selectedItem.pathFromClient}/${data.serverFileName}`, // Usa las comillas invertidas correctas
                                pathFromClient: selectedItem.pathFromClient,
                                uid: uid,
                                serverFileName: data.serverFileName,
                                isLeaf: true,
                                viewName: file.name,
                                typeS: 'SoporteS',
                            }];
                            return {
                                ...updatedNode,
                                children: updatedChildren,
                                pathFromClient: updatedNode.path
                            };
                        }
                        return prevSelectedItem;
                    });
                    updateTreeFilesData(updatedNode);
                }
                onSuccess("ok");
            } else {
                throw new Error(`Error en la carga de ${file.name}`);
            }
        } catch (error) {
            message.error(`${file.name} Error en la carga, vuelve a intentarlo.`);
            onError(error); // Marca la subida como fallida
        }
    };

    const handleFormSubmit = async () => {
        if (!fileToUpload) {
            message.error('Por favor, cargue un archivo.');
            return;
        }
        try {
            await form.validateFields();
            setIsSubmitting(true);
            let titulo;
            const versionFromNumber = Number(versionFrom);
            const resourceType = form.getFieldValue('resourceType');
            if (versionFromNumber !== 13) {
                titulo = form.getFieldValue('titulo');
                if (!titulo) {
                    const allInitialValues = form.getFieldsValue(true);
                    const tituloInicial = allInitialValues.titulo;
                    titulo = tituloInicial;
                }
            } else {
                titulo = selectedItem.name;
            }
            const fileExtension = fileToUpload.name.split('.').pop();
            const newFileName = `${titulo}.${fileExtension}`;
            const decodedFileName = decodeURIComponent(newFileName);
            const renamedFile = new File([fileToUpload], decodedFileName, { type: fileToUpload.type });
            handleCustomRequest({
                file: renamedFile,
                onSuccess: () => {
                    setFileList([]);
                    setFileToUpload(null);
                    setIsModalFileVisible(false);
                    setIsSubmitting(false);

                },
                onError: (error) => {
                    console.error('Error en la subida:', error);
                    setIsSubmitting(false);
                },
                resourceType: resourceType,
            });


        } catch (errorInfo) {
            setIsSubmitting(false);
        }
    };

    const searchInTree = (node, searchTerm, expandedKeys) => {
        let foundMatch = false;
        if (node.viewName.toLowerCase().includes(searchTerm.toLowerCase())) {
            node.highlighted = true;
            foundMatch = true;
            node.title = (
                <span>
                    {node.viewName.replace(
                        new RegExp(`(${searchTerm})`, 'gi'),
                        (text, match) => (
                            <span style={{ backgroundColor: 'yellow', color: '#AAA' }}>{match}</span>
                        )
                    )}
                </span>
            );
        } else {
            node.highlighted = false;
        }
        if (node.children && node.children.length > 0) {
            node.children.forEach(childNode => {
                if (searchInTree(childNode, searchTerm, expandedKeys)) {
                    expandedKeys.push(node.key);
                    foundMatch = true;
                }
            });
        }
        return foundMatch;
    };

    const onChange = (e) => {
        setSearchValue(e.target.value);
    };

    useEffect(() => {
        if (treeData.length > 0) {
            const emptyFolders = filterEmptyFolders(treeData);
            settreeFilesData(emptyFolders);
            setExpandedKeys(prevExpandedKeys => {
                const nodeZeroKey = treeData[0]?.key;
                const updatedExpandedKeys = prevExpandedKeys.includes(nodeZeroKey)
                    ? prevExpandedKeys
                    : [...prevExpandedKeys, nodeZeroKey];
                return updatedExpandedKeys;
            });
        }
    }, [treeData]);

    const onClickBuscar = () => {
        if (searchValue.trim() === '') {
            return;
        }
        const clonedTreeData = cloneDeep(treeData);
        const newExpandedKeys = [];
        clonedTreeData.forEach(node => searchInTree(node, searchValue, newExpandedKeys));
        const nodeZeroKey = treeData[0]?.key;
        if (nodeZeroKey && !newExpandedKeys.includes(nodeZeroKey)) {
            newExpandedKeys.push(nodeZeroKey);
        }
        setTreeData(clonedTreeData);
        setExpandedKeys(newExpandedKeys);
    };

    const handleSelect = async (selectedKeys, info) => {
        // Resuelve siempre el nodo original (sin filtrar) desde treeData, ya
        // que el árbol visible puede estar mostrando la versión filtrada por
        // coincidencias y no debe perderse información al seleccionar.
        const selectedNode = findNode(treeData, info.node.path) || info.node;
        const nombreNodoRaiz = codigo_proyecto_busqueda;
        if (selectedNode.name === nombreNodoRaiz) {
            setRootNodeSelected(true);
            setSelectedItem(null);
        } else {
            setSelectedItem({
                ...selectedNode,
                pathFromClient: selectedNode.path
            });
            setRootNodeSelected(false);

            if (selectedNode.type === 'file') {
                fetchFileUrl(selectedNode);
            }
            setTableParams({
                ...tableParams,
                pagination: {
                    ...tableParams.pagination,
                    total: selectedNode.children ? selectedNode.children.length : 0,
                    current: 1
                }
            });
        }
    };

    const getUploadProps = (pathFromClient) => {
        if (!pathFromClient) {
            const rootNode = treeData[0];
            if (rootNode) {
                setSelectedItem(rootNode);
            }
            return;
        }
        return {
            name: 'file',
            multiple: false,
            listType: 'text',
            openFileDialogOnClick: true,
            showUploadList: true,
            maxCount: 1,
            beforeUpload: (file) => {
                setFileToUpload(file);
                return false;
            },
            onChange(info) {
                const { fileList } = info;
                const file = fileList[fileList.length - 1];
                if (fileList.length > 0) {
                    const updatedFileList = [file];
                    setFileList(prevFileList => {
                        return [file];
                    });
                    setFileList(updatedFileList);
                    if (file.status === 'done') {
                        message.success(`${file.name} archivo cargado exitosamente.`);
                    } else if (file.status === 'error') {
                        message.error(`${file.name} fallo en la carga.`);
                    }
                } else {
                    setFileList([]);
                }
            }

        };
    };

    const [tableParams, setTableParams] = useState({
        pagination: {
            current: 1,
            pageSize: 30,
            total: 0,
        },
    });

    const handleEditFolder = async (path, nuevoNombre) => {
        const pp = codigo_tarea.proyecto;
        const codigActividad = '/actividad_' + quitarPuntos(codigo_tarea.codActividad);
        const tarea = '/tarea_' + quitarPuntos(codigo_tarea.codTarea);
        const indicad = '/indicador_' + codigo_tarea.codIndicador;
        const mvcod = '/medio_verificacion_' + codigo_tarea.codMV;
        const nodeRaiz = 'DocumentosPOA/PlanOperativo/';
        const regex = /^[a-zA-Z0-9 _áéíóúÁÉÍÓÚñÑ.,()-´]+$/;
        if (!regex.test(nuevoNombre)) {
            message.error('El nombre contiene caracteres no permitidos.');
            return;
        }
        const codActividadParametro = pp + codigActividad + tarea + indicad + mvcod;
        const shouldAssignPath = !path.includes('actividad');
        const lastSlashIndex = path.lastIndexOf('/');
        const basePath = path.substring(0, lastSlashIndex);
        const tmpRuta = shouldAssignPath ? `${codActividadParametro}/${basePath}/${nuevoNombre}` : path;
        const oldPath = shouldAssignPath ? `${codActividadParametro}/${path}` : path;
        const fatherlastSlashIndex = tmpRuta.lastIndexOf('/');
        const fatherResource = tmpRuta.substring(0, fatherlastSlashIndex);
        const resourceInfo = await handleAction({
            user: userLogin || 'admin',
            actionType: 'updateFolder',
            nameServer: nuevoNombre,
            resourceDetails: {
                nameResource: nuevoNombre,
                Path: tmpRuta,
                fatherResource: `${nodeRaiz}/${fatherResource}`,
                typeResource: 'folder',
                key: `${nodeRaiz}/${tmpRuta}`,
                fullPath: `${nodeRaiz}/${tmpRuta}`,
                resourceType: 'Folder POA'
            }
        });
        const formData = new FormData();
        formData.append('ruta', oldPath);
        formData.append('nuevoNombre', nuevoNombre);
        formData.append('nodeRaiz', nodeRaiz);
        formData.append('resourceInfo', JSON.stringify(resourceInfo));
        try {
            const response = await axios.post(`${baseUrl}/api/editNameDirectories`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data; charset=UTF-8',
                }
            });
            if (response.status === 200) {
                const tmpRutaResp = response.data.nuevoPath;
                const prefix = `${codActividadParametro}/`;
                const newPath = tmpRutaResp.startsWith(prefix) ? tmpRutaResp.slice(prefix.length) : tmpRutaResp;
                const wasUpdated = await updateNodeName(nuevoNombre, newPath);
                if (wasUpdated) {
                    await reloadTree();
                    message.success('Nombre de la carpeta actualizado correctamente.');
                }
            } else {
                message.error('Error al actualizar el nombre de la carpeta. Inténtalo de nuevo.');
            }
        } catch (error) {
            console.error('Error fetching file tree:', error);
            message.error('Error al actualizar el nombre de la carpeta. Inténtalo de nuevo.');
        }
    };

    const updateNodeName = async (newName, newPath) => {
        if (!selectedItem) {
            return;
        }
        const updatedTreeData = cloneDeep(treeData);
        const updateNode = (nodes, keyToUpdate, newName, newPath) => {
            for (let node of nodes) {
                if (node.key === keyToUpdate) {
                    node.name = newName;
                    node.serverFileName = newName;
                    node.viewName = newName;
                    node.key = newPath;
                    if (node.path) {
                        node.path = newPath;
                    }
                    if (node.children) {
                        updateChildrenPaths(node.children, newPath);
                    }
                    return true;
                }
                if (node.children) {
                    if (updateNode(node.children, keyToUpdate, newName, newPath)) {
                        return true;
                    }
                }
            }
            return false;
        };

        const updateChildrenPaths = (children, newPath) => {
            children.forEach((child) => {
                const newChildPath = `${newPath}/${child.name}`;
                child.path = newChildPath; // Actualizar el path del hijo
                child.key = newChildPath; // Usar el nuevo path como clave

                // Llamar recursivamente si hay más hijos
                if (child.children) {
                    updateChildrenPaths(child.children, newChildPath);
                }
            });
        };


        // Llamar a la función para actualizar el nodo en el árbol clonado
        const wasUpdated = updateNode(updatedTreeData, selectedItem.key, newName, newPath);

        if (wasUpdated) {
            // Actualizar el estado del árbol con los datos actualizados
            setTreeData([...updatedTreeData]);

            const updatedSelectedItem = {
                ...selectedItem,
                name: newName,
                key: newPath,
                path: newPath,
                pathFromClient: newPath,
                viewName: newName,
            };
            setSelectedItem(updatedSelectedItem);

            setTableParams({
                ...tableParams,
                pagination: {
                    ...tableParams.pagination,
                    current: 1, // Reinicia a la primera página
                },
            });

            // Salir del modo de edición
            setIsEditing(false);
        } else {

            message.error('No se pudo actualizar el nodo.');
        }
    };

    const toggleEditMode = () => {
        setIsEditing(!isEditing);
        if (!isEditing) {
            setOriginalName(selectedItem.viewName);
            setNewName(selectedItem.viewName);
        }
    };

    const handleNameChange = (e) => {
        let value = e.target.value.normalize('NFC');
        const regex = /^[a-zA-Z0-9 _áéíóúÁÉÍÓÚñÑ.,()-´]+$/;
        if (regex.test(value) || value === '') {
            setNewFolderName(value);
        } else {
            message.error('El nombre contiene caracteres no permitidos 1.');
        }
    };

    const handleEditNameChange = (e) => {
        const value = e.target.value.normalize('NFC');
        const regex = /^[a-zA-Z0-9 _áéíóúÁÉÍÓÚñÑ.,()-´]+$/;
        if (regex.test(value) || value === '') {
            setNewName(value); // Actualiza el valor del nombre
        } else {
            message.error('El nombre contiene caracteres no permitidos.');
        }
    };

    const cancelEditMode = () => {
        setIsEditing(false);
        setNewName(selectedItem.viewName);
    };


    const generateRandomUid = () => {
        return Math.random().toString(36).substr(2, 9); // Genera una cadena aleatoria de 9 caracteres
    };
    // Función para agregar un archivo al árbol de datos con una clave única
    const addFileToTree = (treeData, pathFromClient, fileToAdd, parentKey) => {
        // Hacer una copia profunda de treeData para no mutar el estado directamente
        const updatedTreeData = JSON.parse(JSON.stringify(treeData));
        // Función recursiva para buscar y agregar el archivo en el árbol
        const addToNode = (nodes, path, file, parentKey) => {
            for (let node of nodes) {
                if (node.path === path) {
                    // Encontrar el nodo correcto y agregar el archivo como hijo
                    if (!node.children) {
                        node.children = [];
                    }

                    // Generar una clave única para el nuevo archivo
                    const newKey = `${node.key} - ${file.uid}`;

                    node.children.push({
                        name: file.name,
                        key: newKey,
                        type: 'file', // Ajustar según la estructura de tu árbol
                        level: node.level + 1, // Ajustar el nivel del nodo según tu estructura
                        path: `${path}/${file.serverFileName}`, // Asegúrate de usar el nombre del servidor aquí
                        pathFromClient: file.pathFromClient, // Agregar pathFromClient
                        uid: file.uid, // Agregar uid
                        serverFileName: file.serverFileName,
                        viewName: file.name,
                        typeS: 'Soporte',
                        isLeaf: true,
                    });
                    return true; // Indicar que se agregó el archivo correctamente
                }
                if (node.children && addToNode(node.children, path, file, parentKey)) {
                    return true; // Si se encontró y se agregó en un nodo hijo
                }
            }
            return false; // No se encontró el nodo para agregar el archivo
        };

        // Llamar a la función para agregar el archivo en el árbol
        addToNode(updatedTreeData, pathFromClient, fileToAdd, parentKey);

        return updatedTreeData; // Devolver el árbol actualizado
    };

    const deleteNodeFromTree = (nodes, pathToDelete) => {
        let parentKey = null;

        const deleteNodeRecursively = (nodes, pathToDelete) => {
            return nodes
                .map(node => {
                    // Mostrar el nodo actual y el path que se quiere eliminar

                    // Verifica que el nodo actual tenga la propiedad 'path'
                    if (node && node.path === pathToDelete) {

                        return null; // Eliminar el nodo actual
                    }

                    // Verifica si el nodo tiene hijos
                    if (Array.isArray(node.children)) {
                        const updatedChildren = deleteNodeRecursively(node.children, pathToDelete);

                        // Verificar si se han eliminado todos los hijos
                        if (updatedChildren.length === 0) {
                            parentKey = node.key;

                            return { ...node, children: [] }; // Mantener nodo vacío si sus hijos están vacíos
                        }


                        return { ...node, children: updatedChildren }; // Devolver nodo con hijos actualizados
                    }

                    // Devolver nodo sin cambios si no tiene hijos
                    return node;
                })
                .filter(node => node !== null); // Filtrar nodos nulos
        };

        const updatedTreeData = deleteNodeRecursively(nodes, pathToDelete);
        return { updatedTreeData, parentKey };
    };

    // Función para encontrar un nodo en el árbol por clave
    const findNodeByKeyF = (nodes, key) => {
        for (const node of nodes) {


            if (node.key === key) {

                return node;
            }
            if (node.children) {
                const foundNode = findNodeByKeyF(node.children, key);
                if (foundNode) {
                    return foundNode;
                }
            }
        }

        return null;
    };

    const handleDeleteFile = async (file) => {
        const pp = codigo_tarea.proyecto;
        const codigActividad = '/actividad_' + quitarPuntos(codigo_tarea.codActividad);
        const tarea = '/tarea_' + quitarPuntos(codigo_tarea.codTarea);
        const indicad = '/indicador_' + codigo_tarea.codIndicador;
        const mvcod = '/medio_verificacion_' + codigo_tarea.codMV;
        const nodeRaiz = 'DocumentosPOA/PlanOperativo';
        const codActividadParametro = pp + codigActividad + tarea + indicad + mvcod;
        // Obtener la ruta del archivo
        const filePath = file.path
            ? file.path.substring(0, file.path.lastIndexOf('/') + 1) + file.serverFileName
            : (file.pathFromClient && file.serverFileName
                ? `${file.pathFromClient}/${file.serverFileName}`
                : null);

        let filePathNode = file.path.substring(0, file.path.lastIndexOf('/') + 1) + file.serverFileName;


        if (!filePath) {
            message.error('No se encontró el nombre del archivo en el servidor.');
            return;
        }

        let tmpFilepathserver = codActividadParametro + '/' + filePath;
        const resourceInfo = await handleAction({
            user: userLogin || 'admin',
            actionType: 'deleteFile',
            nameServer: file.serverFileName,
            resourceDetails: {
                nameResource: file.serverFileName,
                Path: tmpFilepathserver,
                fatherResource: selectedItem.name,
                typeResource: 'file',
                key: file.path,
                fullPath: `${nodeRaiz}/${tmpFilepathserver}`
            }
        });



        // Eliminar el archivo del servidor
        const deleted = await deleteFile(tmpFilepathserver, nodeRaiz, resourceInfo);

        if (deleted) {
            // Actualizar el estado de selectedItem eliminando el archivo
            // Eliminación exitosa en el backend, actualiza el árbol localmente

            const { updatedTreeData, parentKey } = deleteNodeFromTree(treeData, filePathNode);

            // Actualizar el estado del árbol
            setTreeData(updatedTreeData);

            // Seleccionar el nodo padre o el nodo raíz si no tiene padre
            if (parentKey) {
                const parentNode = findNodeByKey(updatedTreeData, parentKey);
                if (parentNode) {
                    setSelectedItem(parentNode);
                }
            } else {
                const rootNode = updatedTreeData[0];
                if (rootNode) {
                    setSelectedItem(rootNode);
                }
            }

            // Si selectedItem está definido, actualizar sus children excluyendo el archivo eliminado
            if (selectedItem) {
                const updatedChildren = selectedItem.children.filter(child => child.path !== filePathNode);
                setSelectedItem({ ...selectedItem, children: updatedChildren });
            }
            setTableParams({
                ...tableParams,
                pagination: {
                    ...tableParams.pagination,
                    current: 1, // Reinicia a la primera página
                },
            });


            message.success('Archivo eliminado correctamente.');
        }
    };

    const deleteFile = async (filePath, nodeRaiz, data) => {
        const formData = new FormData();
        formData.append('filePath', filePath);
        formData.append('nodeRaiz', nodeRaiz);
        formData.append('resourceInfo', JSON.stringify(data));

        try {
            const responsedelete = await axios.post(`${baseUrl}/api/delete-file`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            const data = responsedelete.data;
            if (data.status === 'success') {

                message.success('Archivo eliminado correctamente.');
                return true;
            } else {
                message.error('Error al eliminar el archivo, vuelve a intentarlo.');
                return false;
            }
        } catch (error) {
            message.error('Error al eliminar el archivo, vuelve a intentarlo.');
            return false;
        }
    };

    const descriptionDirectories = [
        { key: "Ficha para la revisión", description: "Ficha para la revisión de iniciativas a estructurar por ART." },
        { key: "Priorización de territorios", description: "Priorización de territorios, cadenas de valor y comunidades para proyectos estructurados según el PME y otras fuentes." },
        { key: "Acta Paso 4", description: "Acta de mesa técnica con ART del Paso 4." },
        { key: "Documento de análisis", description: "Documento de análisis para definición de potenciales alcances y socios del proyecto a estructurar." },
        { key: "Acta Paso 5", description: "Acta de mesa técnica con ART del Paso 5." },
        { key: "Asistencia taller Marco Lógico", description: "Listado de asistencia desagregados por sexo y pertenencia étnica del Taller." },
        { key: "Memoria Marco Lógico", description: "Memoria de Reunión del Taller de insumos de Marco Lógico con comunidad." },
        { key: "Documento del Proyecto", description: "Documento de identificación general del proyecto." },
        { key: "Acta Paso 6", description: "Acta de mesa técnica con ART del Paso 6." },
        { key: "Caracterización de población", description: "Caracterización de población participante." },
        { key: "Instrumento MER", description: "Instrumento Medición del Emprendimiento Rural – MER" },
        { key: "Formato de campo", description: "Formato de campo diligenciado para el componente de infraestructura." },
        { key: "Ficha de evaluación técnica", description: "Ficha de evaluación técnica para proyectos de infraestructura productiva diligenciada." },
        { key: "Homologación MER-ICO", description: "Homologación MER-ICO aplicado" },

    ];

    const extractDateFromFilename = (filename) => {
        // Usa una expresión regular para encontrar la fecha en el nombre del archivo
        const dateMatch = filename.match(/(\d{2})(\d{2})(\d{4})/);
        if (dateMatch) {
            // Convierte la fecha a un formato que se pueda comparar fácilmente
            const [_, day, month, year] = dateMatch;
            return new Date(`${year}-${month}-${day}`);
        }
        return new Date(0); // Devuelve una fecha predeterminada si no se encuentra una fecha
    };

    // Ordena los hijos de una carpeta: carpetas primero, luego archivos por la
    // fecha embebida en su nombre (ascendente, el más antiguo primero).
    const ordenarHijosPorFecha = (children) => {
        if (!Array.isArray(children)) return children;
        const carpetas = children.filter((c) => c.type === 'folder');
        const archivos = children.filter((c) => c.type !== 'folder');
        const archivosOrdenados = [...archivos].sort((a, b) => {
            const dateA = extractDateFromFilename(cleanFileName(a.viewName || a.name || ''));
            const dateB = extractDateFromFilename(cleanFileName(b.viewName || b.name || ''));
            return dateA - dateB;
        });
        return [...carpetas, ...archivosOrdenados];
    };


    const normalizeString = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    const getDirectoryDescription = () => {
        if (selectedItem && selectedItem.type === 'folder') {
            const selectedKey = normalizeString(selectedItem.name); // Normaliza el nombre del directorio seleccionado
            const directory = descriptionDirectories.find(dir => {
                const normalizedKey = normalizeString(dir.key);
                return normalizedKey === selectedKey;
            });

            return directory ? directory.description : ""; // Retornar la descripción si se encuentra, de lo contrario cadena vacía
        }
        return "";
    };

    const handleTableChange = (pagination) => {
        setTableParams({
            ...tableParams,
            pagination
        });
    };


    const renderSelectedFiles = () => {
        if (selectedItem && selectedItem.type === 'folder') {
            const dataSourceArchivos = mostrarFiltroCoincidencias
                ? aplicarFiltroCoincidencias(ordenarHijosPorFecha(selectedItem.children), filtroCoincidencias)
                : ordenarHijosPorFecha(selectedItem.children);
            return (
                <>
                    <Table
                        dataSource={dataSourceArchivos}
                        pagination={tableParams.pagination}
                        onChange={handleTableChange}
                        scroll={{
                            y: 340,
                        }}
                        columns={[

                            {
                                title: 'Opciones',
                                key: 'action',
                                width: 200,
                                align: 'center',
                                render: (text, record) => (
                                    <Space size="middle">
                                        {permissionExport && (
                                            <Tooltip title="Descargar">
                                                <Popconfirm
                                                    title='Descargar archivos'
                                                    description="¿Segur@ que quieres descargar el directorio?"
                                                    onConfirm={() => handleDescargar(record)}
                                                    okText="Sí"
                                                    cancelText="No"
                                                    okButtonProps={{
                                                        loading: loading,
                                                    }}
                                                >
                                                    <Button
                                                        type="link"
                                                        icon={<DownloadOutlined style={{ fontSize: '1.1rem' }} />}
                                                        rel="noopener noreferrer"
                                                        className="ico_table"
                                                    />

                                                </Popconfirm>
                                            </Tooltip>
                                        )}
                                        {habilitarExportarArbol && record.type === 'folder' && (
                                            <Tooltip title="Exportar árbol de esta carpeta a PDF">
                                                <Button
                                                    type="link"
                                                    icon={<ApartmentOutlined style={{ fontSize: '1.1rem' }} />}
                                                    className="ico_table"
                                                    onClick={() => handleExportarArbolCarpeta(record)}
                                                />
                                            </Tooltip>
                                        )}
                                        {record.type === 'file' && (
                                            <>
                                                {permissionDelete && (
                                                    <Tooltip title="Eliminar archivo">
                                                        <Popconfirm
                                                            title='Eliminación de archivo'
                                                            description="¿Segur@ que quieres eliminar este archivo?"
                                                            onConfirm={() => handleDeleteFile(record)}
                                                            okText="Sí"
                                                            cancelText="No"
                                                            icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                                                        >
                                                            <Button
                                                                type="link"
                                                                danger
                                                                icon={<DeleteOutlined />}
                                                                className='ico_table'
                                                            />
                                                        </Popconfirm>
                                                    </Tooltip>
                                                )}

                                                <div>
                                                    <Tooltip title="Visualizar el archivo">
                                                        <Button onClick={() => fetchFileUrl(record)} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '10px !important' }}>
                                                            <EyeOutlined style={{ fontSize: '24px', color: '#08c' }} />
                                                        </Button>
                                                    </Tooltip>
                                                </div>
                                            </>
                                        )}

                                        {record.canDelete && permissionDelete && (

                                            <Tooltip title="Eliminar del directorio">
                                                <Popconfirm
                                                    title='Eliminación de directorio'
                                                    description="¿Segur@ que quieres eliminar este directorio, no podras revertir esta operación?"
                                                    onConfirm={() => handleDeleteFolder(record.path, record)}
                                                    okText="Sí"
                                                    cancelText="No"
                                                    icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                                                >
                                                    <Button
                                                        type="link"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        className='me-2 p-0'

                                                    />
                                                </Popconfirm>
                                            </Tooltip>
                                        )
                                        }

                                    </Space>
                                ),
                            },
                            {
                                title: 'Documento',
                                dataIndex: 'name',
                                key: 'key',
                                width: 400,
                                sorter: (a, b) => {
                                    // Extrae y compara las fechas de los nombres de los archivos
                                    const dateA = extractDateFromFilename(cleanFileName(a.viewName || a.name || ''));
                                    const dateB = extractDateFromFilename(cleanFileName(b.viewName || b.name || ''));
                                    return dateB - dateA; // Ordenar del más reciente al más antiguo
                                },
                                render: (text, record) => {
                                    const maxLength = 50;
                                    const maxLengthTooltip = 200;
                                    let truncatedTitle;

                                    if (record.type === 'file') {
                                        truncatedTitle = processFileName(record.viewName, maxLength);
                                    } else {
                                        truncatedTitle = record.viewName;
                                    }
                                    const truncatedTooltip = processFileName(record.viewName, maxLengthTooltip);

                                    return (
                                        <Tooltip title={truncatedTooltip} placement="right">
                                            <span>
                                                {record.type === 'folder' ? <FolderOutlined /> : <FileOutlined />}
                                                {` ${truncatedTitle}`}
                                            </span>
                                        </Tooltip>
                                    );
                                }
                            },
                            {
                                title: 'Formato',
                                key: 'extension',
                                width: 100,
                                align: 'center',
                                render: (text, record) => {
                                    // Verifica si record.name es una cadena de texto
                                    if (typeof record.name === 'string') {
                                        if (record.type === 'folder' && typeof record.fileCount !== 'undefined') {
                                            const pathContainsTarea = record.path.includes("tarea_221/indicador_34/");
                                            const fileCountToDisplay = pathContainsTarea ? (record.fileCount - record.filePhoto === 0 ? record.fileCount : record.fileCount - record.filePhoto) : record.fileCount;
                                            return (
                                                <>
                                                    <Tooltip title="Cantidad de archivos que contiene la carpeta">
                                                        <span>Folder</span> <span></span>
                                                        <Badge
                                                            className="site-badge-count-109"
                                                            count={fileCountToDisplay}
                                                            overflowCount={9999}
                                                            style={{
                                                                backgroundColor: '#52c41a',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </>
                                            );
                                        } else if (record.type === 'folder' && record.children.some(child => child.type === 'file')) {
                                            return (
                                                <>
                                                    <Tooltip title="Cantidad de archivos que contiene la carpeta">
                                                        <span>Folder</span> <span>  </span>
                                                        <Badge
                                                            className="site-badge-count-109"
                                                            count={record.children.length}
                                                            style={{
                                                                backgroundColor: '#52c41a',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </>
                                            );
                                        } else {
                                            if (record.type === 'folder') {
                                                return 'Folder'; // O cualquier otro mensaje adecuado para carpetas vacías
                                            } else {
                                                const fileNameParts = record.name.split('.');
                                                return fileNameParts.length > 1 ? fileNameParts.pop() : 'Archivo';
                                            }
                                        }
                                    } else {
                                        // Maneja el caso en que record.name no es una cadena de texto
                                        return 'Desconocido'; // O cualquier otro valor predeterminado adecuado
                                    }
                                },
                            },
                            {
                                title: 'Tipo de soporte',
                                dataIndex: 'name',
                                key: 'key',
                                width: 150,
                                render: (text, record) => {
                                    let tipoSoporte = '-'; // Valor por defecto

                                    if (record.metadato) {
                                        try {
                                            // Parseamos detailJSON para convertirlo en un objeto
                                            const detailData = JSON.parse(record.metadato.detailJSON);

                                            // Accedemos a resourceType
                                            tipoSoporte = detailData.resourceData ? detailData.resourceData.resourceType : 'No reportado';
                                        } catch (error) {
                                            console.error('Error al parsear detailJSON:', error);
                                            tipoSoporte = 'Error en datos';
                                        }
                                    }

                                    return (
                                        <span>
                                            {tipoSoporte}
                                        </span>
                                    );
                                }
                            },
                            {
                                title: 'Usuario',
                                dataIndex: 'name',
                                key: 'key',
                                width: 150,
                                render: (text, record) => {

                                    const usuario = record.metadato ? record.metadato.user : '-'; // Puedes ajustar el texto en caso de que sea null

                                    return (
                                        <span>
                                            {usuario}
                                        </span>
                                    );
                                }
                            },
                            {
                                title: 'Creación',
                                dataIndex: 'name',
                                key: 'key',
                                width: 150,
                                render: (text, record) => {
                                    // Obtenemos la fecha y la formateamos
                                    const fecha = record.metadato ? record.metadato.dateTime : '-'; // Obtiene la fecha
                                    const creacion = fecha !== '-' ? new Date(fecha).toISOString().replace('T', ' ').substring(0, 19) : '-'; // Formateo

                                    return (
                                        <span>
                                            {creacion}
                                        </span>
                                    );
                                }
                            },


                        ]}
                    />
                </>
            );
        } else {
            return (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <WarningOutlined style={{ color: 'red', marginRight: '8px', fontSize: '2rem' }} />
                    <h3>Seleccione el siguiente nivel del árbol para ver los archivos.</h3>
                </div>
            );
        }
    };

    // Función para volver a cargar el árbol
    const reloadTree = async () => {
        setLoading(true); // Asegúrate de establecer el estado de carga si es necesario
        try {
            const tmpcodActividadParametro = String(codigo_tarea.codActividad).replace(/\./g, '');
            const codActividadParametro = 'actividad_' + tmpcodActividadParametro;
            const response = await axios.get(`${baseUrl}/api/directorytreepoa?codigActividad=${codActividadParametro}&codigoTarea=${codigo_proyecto_busqueda}`);
            const data = response.data;

            const mv = data[0].children[0].children[0].name; // Nombre medio Verificacion
            const indicador = codigo_tarea.key;
            const fullPath = "DocumentosPOA/PlanOperativo//" + codActividadParametro + "/" + codigo_proyecto_busqueda + "/" + indicador + "/" + mv;
            const documents = await fetchPoaDataByPath(fullPath);

            setDataFile(documents);


            // Función para filtrar y obtener solo los children de los nodos tipo 'indicador'
            const getIndicadorChildren = (nodes) => {
                return nodes.flatMap(node => {
                    if (node.typeS === 'indicador') {
                        return node.children;
                    } else if (node.children && node.children.length > 0) {
                        return getIndicadorChildren(node.children);
                    }
                    return [];
                });
            };

            const filteredData = getIndicadorChildren(data);

            // Actualiza los datos del árbol
            setTreeData(filteredData);

            // Expandir solo el nodo raíz
            // Conservar el selectedItem actual o actualizarlo si sigue en el nuevo árbol
            if (selectedItem) {
                const existingNode = findNodeByKeyF(filteredData, selectedItem.key);
                if (existingNode) {
                    setSelectedItem(existingNode); // Mantiene el nodo seleccionado si existe
                    setExpandedKeys([existingNode.key]); // Expande el nodo encontrado
                } else {
                    setSelectedItem(null); // O establece a null si no existe
                    setExpandedKeys([filteredData[0]?.key]); // Expande el nodo raíz si no hay nodo seleccionado
                }
            } else {
                // Si no hay selectedItem, expande solo el nodo raíz
                setExpandedKeys([filteredData[0]?.key]);
            }


        } catch (error) {
            console.error('Error fetching file tree:', error);
        } finally {
            setLoading(false);
        }
    };

    const onLimpiarBusqueda = async () => {
        setActiveButton(null);
        setSearchValue('');
        setSearchValueCod('');
        setRootNodeSelected(true);
        setSelectedItem(null);
        setLoading(true);

        try {


            const pp = codigo_tarea.proyecto;
            const codigActividad = 'actividad_' + quitarPuntos(codigo_tarea.codActividad);
            const tarea = 'tarea_' + quitarPuntos(codigo_tarea.codTarea);
            const indicad = 'indicador_' + codigo_tarea.codIndicador;
            const mvcod = 'medio_verificacion_' + codigo_tarea.codMV;
            const response = await axios.get(`${baseUrl}/api/directorytreepoaPP?proyecto=${pp}&codigActividad=${codigActividad}&codigoTarea=${tarea}&codigoIndicador=${indicad}&mvcod=${mvcod}`);

            const data = response.data;

            const indicador = codigo_tarea.codIndicador;
            const fullPath = "DocumentosPOA/PlanOperativo/" + codigActividad + "/" + codigo_proyecto_busqueda + "/" + indicador + "/";
            const documents = await fetchPoaDataByPath(fullPath);

            setDataFile(documents);


            // Función para filtrar y obtener solo los children de los nodos tipo 'indicador'
            const getIndicadorChildren = (nodes) => {
                return nodes.flatMap(node => {
                    if (node.typeS === 'indicador') {
                        return node.children;
                    } else if (node.children && node.children.length > 0) {
                        return getIndicadorChildren(node.children);
                    }
                    return [];
                });
            };

            const filteredData = getIndicadorChildren(data);

            // Actualiza los datos del árbol
            setTreeData(data);

            // Expandir solo el nodo raíz
            setExpandedKeys([filteredData[0]?.key]);

        } catch (error) {
            console.error('Error fetching file tree:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteFolder = async (path, data) => {
        const pp = codigo_tarea.proyecto;
        const codigActividad = '/actividad_' + quitarPuntos(codigo_tarea.codActividad);
        const tarea = '/tarea_' + quitarPuntos(codigo_tarea.codTarea);
        const indicad = '/indicador_' + codigo_tarea.codIndicador;
        const mvcod = '/medio_verificacion_' + codigo_tarea.codMV;

        const nodeRaiz = 'DocumentosPOA/PlanOperativo/';
        const tmpcodActividadParametro = String(codigo_tarea.codActividad).replace(/\./g, '');
        const codActividadParametro = pp + codigActividad + tarea + indicad + mvcod;
        const tmpRuta = `${codActividadParametro}/${path}`;

        try {

            const nameServer = data.serverFileName && data.serverFileName.trim() !== '' ? data.serverFileName : data.name;

            const resourceInfo = await handleAction({
                user: userLogin || 'admin', // Reemplaza con el usuario actual
                actionType: 'deleteFolder',
                nameServer: nameServer, // Nombre del servidor
                resourceDetails: {
                    nameResource: nameServer,
                    Path: tmpRuta,
                    fatherResource: data.name,
                    typeResource: 'folder',
                    key: data.path,
                    fullPath: `${nodeRaiz}/${data.path}`
                }
            });

            // Convertir resourceInfo a JSON string y usar FormData
            const formData = new FormData();
            formData.append('dirPath', tmpRuta);
            formData.append('nodeRaiz', nodeRaiz);
            formData.append('resourceInfo', JSON.stringify(resourceInfo));

            const response = await axios.post(`${baseUrl}/api/delete-directory`, formData);

            if (response.status === 200) {
                // Eliminación exitosa en el backend, actualiza el árbol localmente
                const { updatedTreeData, parentKey } = deleteNodeFromTree(treeData, path);

                // Actualizar el estado del árbol
                setTreeData(updatedTreeData);

                // Seleccionar el nodo padre o el nodo raíz si no tiene padre
                if (parentKey) {
                    const parentNode = findNodeByKey(updatedTreeData, parentKey);
                    if (parentNode) {
                        setSelectedItem(parentNode);
                    }
                } else {
                    const rootNode = updatedTreeData[0];
                    if (rootNode) {
                        setSelectedItem(rootNode);
                    }
                }

                // Si selectedItem está definido, actualizar sus children excluyendo el archivo eliminado
                if (selectedItem) {
                    const updatedChildren = selectedItem.children.filter(child => child.path !== path);
                    setSelectedItem({ ...selectedItem, children: updatedChildren });
                }
                setTableParams({
                    ...tableParams,
                    pagination: {
                        ...tableParams.pagination,
                        current: 1, // Reinicia a la primera página
                    },
                });

                message.success('Directorio y sus contenidos eliminados correctamente.');
            } else {
                message.error('Error al eliminar el directorio y sus contenidos. Inténtalo de nuevo.');
            }
        } catch (error) {
            console.error('Error al eliminar el directorio:', error);
            message.error('Error al eliminar el directorio y sus contenidos. Inténtalo de nuevo.');
        }
    };

    // Función para encontrar un nodo por su clave en el árbol
    const findNodeByKey = (nodes, key) => {
        for (const node of nodes) {
            if (node.key === key) {
                return node;
            }
            if (node.children) {
                const found = findNodeByKey(node.children, key);
                if (found) return found;
            }
        }
        return null;
    };

    // Modificación en deleteNodeFromTree para retornar el parentKey

    const findNode = (nodes, path) => {
        // Recorrer los nodos para encontrar el nodo con la ruta proporcionada
        for (let node of nodes) {
            if (node.path === path) {
                return node; // Devolver el nodo si se encuentra
            } else if (node.children) {
                // Si tiene hijos, buscar recursivamente en los hijos
                const foundNode = findNode(node.children, path);
                if (foundNode) return foundNode; // Devolver el nodo encontrado en los hijos
            }
        }
        return null; // Devolver null si no se encuentra el nodo con la ruta especificada
    };

    const titleRenderer = (node) => {
        const maxLength = 20; // Define el número máximo de caracteres
        let decodedFileName;

        let nombre;
        let truncatedTitle;

        if (node.type === 'folder') {

            nombre = node.viewName;
            truncatedTitle = truncateText(nombre, maxLength);;

        } else {

            decodedFileName = decodeURIComponent(node.viewName);
            nombre = cleanFileName(decodedFileName);
            truncatedTitle = truncateText(nombre, maxLength);

        }

        return (
            <Tooltip title={nombre} placement="right" style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                <span
                    style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}
                    className={node.highlighted ? 'highlightedNode' : ''}
                >
                    {truncatedTitle}
                </span>
            </Tooltip>
        );
    };

    const fetchFileUrl = async (record) => {
        const pp = codigo_tarea.proyecto;
        const codigActividad = '/actividad_' + quitarPuntos(codigo_tarea.codActividad);
        const tarea = '/tarea_' + quitarPuntos(codigo_tarea.codTarea);
        const indicad = '/indicador_' + codigo_tarea.codIndicador;
        const mvcod = '/medio_verificacion_' + codigo_tarea.codMV;
        const nodeRaiz = 'DocumentosPOA/PlanOperativo/';
        const codActividadParametro = pp + codigActividad + tarea + indicad + mvcod;
        const tmpRuta = codActividadParametro + '/' + record.path;

        const formData = new FormData();
        formData.append('ruta', tmpRuta);
        formData.append('nodeRaiz', nodeRaiz);

        try {
            const response = await axios.post(`${baseUrl}/api/descargar-archivos`, formData, {
                responseType: 'blob',
            });
            const mimeType = response.headers['content-type'] || 'application/octet-stream';
            const blob = new Blob([response.data], { type: mimeType });
            const blobUrl = window.URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
        } catch (error) {
            message.error('Error al visualizar el archivo');
        }
    };

    // Lógica de filtrado sin archivos
    const filterEmptyFolders = (treeData) => {
        // Función para determinar si un nodo es una carpeta final sin archivos
        const isEmptyFinalFolder = (node) => {

            if (node.type !== 'folder') {
                // Si es un archivo, no se considera como una carpeta final vacía

                return false;
            }

            // Si es una carpeta final sin archivos
            if (!node.children || node.children.length === 0) {

                return true;
            }


            // Verificar si todas las subcarpetas no tienen archivos
            const allSubfoldersEmpty = node.children.every(child => isEmptyFinalFolder(child));

            return allSubfoldersEmpty;
        };

        // Función para filtrar el árbol, manteniendo las ramas que contienen carpetas finales vacías
        const filterTree = (nodes) => {
            return nodes
                .map(node => {

                    // Filtrar los hijos recursivamente
                    const filteredChildren = node.children ? filterTree(node.children) : [];


                    // Determinar si la carpeta debe ser mantenida
                    if (node.type === 'folder') {
                        // Mantener la carpeta si es una carpeta final vacía o si tiene hijos que no se deben eliminar
                        const shouldKeep = isEmptyFinalFolder(node) || filteredChildren.length > 0;

                        return shouldKeep ? { ...node, children: filteredChildren } : null;
                    }

                    // Si es un archivo, no se debe mantener

                    return null;
                })
                .filter(node => node !== null); // Elimina nodos nulos
        };

        // Filtrar los datos del árbol a partir de la raíz
        const filteredTreeData = filterTree(treeData);


        return filteredTreeData;
        // setTreeData(filteredTreeData); // Actualiza el estado con los datos filtrados
    };

    //Logica del LOG        
    const handleAction = async ({ user, actionType, nameServer, resourceDetails }) => {
        // Usa logAction para crear el JSON
        const logEntry = logAction(user, actionType, nameServer, resourceDetails);
        return logEntry
    };

    // Lógica de filtrado con archivos
    const filterFolders = () => {
        // Función para determinar si un nodo tiene archivos en su subárbol
        const hasFiles = (node) => {
            if (node.type === 'file') return true;

            if (node.children && node.children.length > 0) {
                return node.children.some(child => hasFiles(child));
            }

            return false;
        };

        // Usar una pila para procesar los nodos iterativamente
        const stack = [...treeData];
        const foldersToRemove = new Set(); // Conjunto de carpetas que deben eliminarse

        // Primer paso: procesar nodos para identificar carpetas sin archivos
        while (stack.length > 0) {
            const node = stack.pop();

            if (node.type === 'folder') {
                if (node.children) {
                    // Agregar los hijos a la pila
                    node.children.forEach(child => stack.push(child));

                    // Marcar la carpeta para eliminar si no tiene archivos en su subárbol
                    if (!hasFiles(node)) {
                        foldersToRemove.add(node);
                    }
                }
            }
        }

        // Función para filtrar el árbol basado en las carpetas que deben eliminarse
        const filterTree = (nodes) => {
            return nodes
                .map(node => {
                    // Filtrar los hijos recursivamente
                    const filteredChildren = node.children ? filterTree(node.children) : [];

                    // Si el nodo es una carpeta, se mantiene si no debe eliminarse
                    if (node.type === 'folder') {
                        const shouldKeep = node === treeData[0] || !foldersToRemove.has(node);
                        return shouldKeep ? { ...node, children: filteredChildren } : null;
                    }

                    // Mantener archivos
                    return node;
                })
                .filter(node => node !== null); // Elimina nodos nulos
        };

        // Filtrar los datos del árbol a partir de la raíz
        const filteredTreeData = filterTree(treeData);

        return filteredTreeData;
        // setTreeData(filteredTreeData); // Actualiza el estado con los datos filtrados
    };

    // Handler para el botón
    const handleFilterEmptyFolders = () => {

        const filteredData = filterEmptyFolders(treeData);
        setTreeData(filteredData);
        setActiveButton('emptyFolders');


    };

    // Handler para el botón
    const handleFilterFolders = () => {

        const filteredData = filterFolders(treeData);
        setTreeData(filteredData);
        setActiveButton('folders');

    };

    const treeContainerStyle = {
        height: '500px', // Ajusta esta altura según tus necesidades
        overflowY: 'auto', // Habilita el scroll vertical
        border: '1px solid #d9d9d9', // Opcional: añade un borde para definir el área del tree
        padding: '8px', // Opcional: añade un padding interno
        boxSizing: 'border-box' // Asegura que el padding no aumente el tamaño del contenedor
    };

    const handleCreateFolder = async () => {
        try {
            const pp = codigo_tarea.proyecto;
            const codigActividad = '/actividad_' + quitarPuntos(codigo_tarea.codActividad);
            const tarea = '/tarea_' + quitarPuntos(codigo_tarea.codTarea);
            const indicad = '/indicador_' + codigo_tarea.codIndicador;
            const mvcod = '/medio_verificacion_' + codigo_tarea.codMV;


            const nodeRaiz = 'DocumentosPOA/PlanOperativo';
            const codActividadParametro = pp + codigActividad + tarea + indicad + mvcod;
            const tmpRuta = `${codActividadParametro}/${selectedItem.path}`;
            const response = await checkFolderName(tmpRuta, newFolderName, nodeRaiz);

            if (response.data.status === 'error') {
                message.error(response.data.message);
                return;
            }

            const resourceInfo = await handleAction({
                user: userLogin || "admin",
                actionType: 'createFolder',
                nameServer: newFolderName,
                resourceDetails: {
                    nameResource: newFolderName,
                    Path: tmpRuta,
                    fatherResource: selectedItem.name,
                    typeResource: 'folder',
                    key: `${selectedItem.path}/${newFolderName}`,
                    fullPath: `${nodeRaiz}/${tmpRuta}/${newFolderName}`
                }
            });
            await createFolder(tmpRuta, newFolderName, nodeRaiz, resourceInfo);
            message.success('Carpeta creada exitosamente');
            const parentPath = selectedItem.path || '';
            addNodeToTree(parentPath, newFolderName);
            const updatedNode = findNode(treeData, parentPath);
            if (updatedNode) {
                setSelectedItem(prevSelectedItem => {
                    if (prevSelectedItem.path === updatedNode.path) {
                        const updatedChildren = [...updatedNode.children, {
                            name: newFolderName,
                            key: `${parentPath}/${newFolderName}`,
                            path: `${parentPath}/${newFolderName}`,
                            type: 'folder',
                            isLeaf: false,
                            children: [],
                            viewName: newFolderName,
                            canDelete: true,
                        }];
                        return {
                            ...updatedNode,
                            children: updatedChildren,
                            pathFromClient: updatedNode.path
                        };
                    }
                    return prevSelectedItem;
                });
                updateTreeFilesData(updatedNode);
            }
            setIsModalVisible(false);
            setNewFolderName('');
        } catch (error) {
            console.error('Error al crear la carpeta:', error);
            message.error('Ocurrió un error al crear la carpeta, verifique que el nombre de la Carpeta no exista.');
        }
    };


    const updateTreeFilesData = (node) => {
        if (node && node.children) {
            // Asignar los hijos del nodo seleccionado a treeFilesData
            settreeFilesData(node.children);

        } else {
            // Si no hay nodos hijos, puedes limpiar o manejar el estado de otra manera
            settreeFilesData([]);

        }
    };

    useEffect(() => {
        renderSelectedFiles();
    }, [dataFile]); // Escucha cambios en dataFile



    const addNodeToTree = (path, newFolderName) => {
        // Realizar una copia profunda de los datos del árbol para evitar mutaciones directas
        const updatedTreeData = cloneDeep(treeData);

        // Función recursiva para buscar y agregar un nuevo nodo en el árbol
        const addNode = (nodes, pathToUpdate, newFolderName) => {
            return nodes.map(node => {
                if (node.path === pathToUpdate) {
                    // Asegurarse de que node.children esté definido y sea un array
                    const children = node.children || [];
                    const newNode = {
                        name: newFolderName,
                        key: `${pathToUpdate}/${newFolderName}`,
                        path: `${pathToUpdate}/${newFolderName}`,
                        type: 'folder',
                        isLeaf: false, // Indicar que es una carpeta (no una hoja)
                        children: [], // Inicialmente sin hijos
                        edit: true,
                        canDelete: true,
                        viewName: newFolderName,
                        typeS: 'Desconocido',
                    };

                    return {
                        ...node,
                        children: [...children, newNode],
                    };
                } else if (node.children) {
                    // Recursivamente buscar en los hijos
                    return {
                        ...node,
                        children: addNode(node.children, pathToUpdate, newFolderName),
                    };
                }
                return node;
            });
        };

        // Llamar a la función para agregar el nodo en el árbol clonado
        const newTreeData = addNode(updatedTreeData, path, newFolderName);

        // Actualizar el estado del árbol con los datos actualizados
        setTreeData(newTreeData);
    };

    // Asegúrate de que selectedItem no sea null o undefined
    const typeS = selectedItem?.typeS || '';

    // Convertir a mayúsculas y eliminar guiones bajos
    const formattedTypeS = typeS
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/^\w/, c => c.toUpperCase());

    // Inicializar el valor predeterminado
    let medioVerificacion = "Medio de Verificación";

    // Asegúrate de que las propiedades existen antes de intentar acceder a ellas
    if (codigo_tarea && codigo_tarea.codigo_tarea && Array.isArray(codigo_tarea.medioV)) {
        // Obtener el array de medioV
        const medioVArray = codigo_tarea.medioV;

        // Extraer los valores de medioVerificacion de cada objeto en el array
        const medioVerificaciones = medioVArray.map(item => item.medioVerificacion);

        // Si hay valores en el array, asignar el primero (o todos si prefieres)
        if (medioVerificaciones.length > 0) {
            medioVerificacion = medioVerificaciones.join(', '); // Unir los valores con una coma, o solo usa [0] para el primer valor

        }
    }

    const handleDescargar = async (node) => {
        const pp = codigo_tarea.proyecto;
        const codigActividad = '/actividad_' + quitarPuntos(codigo_tarea.codActividad);
        const tarea = '/tarea_' + quitarPuntos(codigo_tarea.codTarea);
        const indicad = '/indicador_' + codigo_tarea.codIndicador;
        const mvcod = '/medio_verificacion_' + codigo_tarea.codMV;

        const nodeRaiz = 'DocumentosPOA/PlanOperativo/';
        const tmpcodActividadParametro = String(codigo_tarea.codActividad).replace(/\./g, '');
        const codActividadParametro = pp + codigActividad + tarea + indicad + mvcod;
        const tmpRuta = codActividadParametro + '/' + node.path;

        const formData = new FormData();
        formData.append('ruta', tmpRuta); // Obtener la ruta del nodo
        formData.append('nodeRaiz', nodeRaiz);

        try {
            const response = await axios.post(`${baseUrl}/api/descargar-archivos`, formData, {
                responseType: 'blob', // Indicar que la respuesta es un blob (archivo)
            });

            // Crear un objeto URL para el blob y crear un enlace de descarga
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;

            // Verificar el tipo de nodo y ajustar el nombre del archivo
            if (node.type === 'folder') {
                a.download = `${node.viewName}.zip`; // Nombre del archivo para descargar si es una carpeta
            } else {
                // Si es un archivo, intenta mantener la extensión original
                const contentDisposition = response.headers['content-disposition'];
                let filename = processFileName(node.viewName, 1000);
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                    if (filenameMatch && filenameMatch.length === 2) {
                        filename = filenameMatch[1];
                    }
                }
                a.download = filename; // Nombre del archivo para descargar si es un archivo
            }

            a.click();

            // Liberar el objeto URL creado
            window.URL.revokeObjectURL(url);
            message.info('Descarga completa de archivos');
        } catch (error) {
            message.error('Error al descargar archivos');
        }
    };

    // --- Exportación del árbol de carpetas/archivos a PDF (solo Informe Técnico Final) ---

    const sanitizarNombreArchivo = (texto) => {
        return (texto || 'arbol')
            .toString()
            .normalize('NFD').replace(/[̀-ͯ]/g, '')
            .replace(/[\\/:*?"<>|]+/g, '_')
            .trim();
    };

    const obtenerEtiquetaNodoArbol = (nodo) => {
        let nombre = nodo.viewName || nodo.name || '';
        try {
            nombre = cleanFileName(nombre);
        } catch {
            // El nombre no está codificado como URI, se deja tal cual.
        }
        nombre = truncateText(nombre, 80);
        return nodo.type === 'folder' ? `${nombre}/` : nombre;
    };

    const construirLineasArbol = (nodo, esRaiz = false, prefijo = '') => {
        const lineas = [];
        if (esRaiz) {
            lineas.push(obtenerEtiquetaNodoArbol(nodo));
        }
        const hijos = nodo.children || [];
        hijos.forEach((hijo, index) => {
            const esUltimo = index === hijos.length - 1;
            const conector = esUltimo ? '`-- ' : '|-- ';
            lineas.push(`${prefijo}${conector}${obtenerEtiquetaNodoArbol(hijo)}`);
            if (hijo.children && hijo.children.length > 0) {
                const nuevoPrefijo = prefijo + (esUltimo ? '    ' : '|   ');
                lineas.push(...construirLineasArbol(hijo, false, nuevoPrefijo));
            }
        });
        return lineas;
    };

    const generarPDFArbol = (nodoRaiz, titulo, subtitulos, nombreArchivo) => {
        if (!nodoRaiz) {
            message.warning('No hay información de carpetas para exportar.');
            return;
        }
        const doc = new jsPDF({ unit: 'pt', format: 'a4' });
        const margenIzquierdo = 40;
        const altoUtil = doc.internal.pageSize.getHeight() - 40;
        let cursorY = 50;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text(titulo, margenIzquierdo, cursorY);
        cursorY += 20;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        subtitulos.forEach((linea) => {
            doc.text(linea, margenIzquierdo, cursorY);
            cursorY += 14;
        });
        cursorY += 10;

        doc.setFont('courier', 'normal');
        doc.setFontSize(9);
        construirLineasArbol(nodoRaiz, true).forEach((linea) => {
            if (cursorY > altoUtil) {
                doc.addPage();
                cursorY = 50;
                doc.setFont('courier', 'normal');
                doc.setFontSize(9);
            }
            doc.text(linea, margenIzquierdo, cursorY);
            cursorY += 13;
        });

        doc.save(nombreArchivo);
        message.success('Árbol exportado a PDF correctamente.');
    };

    const obtenerSubtitulosContextoArbol = () => ([
        `Actividad: ${codigo_tarea.codActividad} - ${codigo_tarea.actividad}`,
        `Tarea: ${codigo_tarea.tarea}`,
        `Indicador: ${codigo_tarea.indicador}`,
        `Medio de verificación: ${codigo_tarea.medioVerificacion}`,
    ]);

    const handleExportarArbolCompleto = () => {
        if (!treeData || treeData.length === 0) {
            message.warning('No hay carpetas cargadas para exportar.');
            return;
        }
        const nodoRaiz = treeData.length === 1
            ? treeData[0]
            : { name: 'Árbol completo', type: 'folder', children: treeData };
        const nombreArchivo = `Arbol_completo_${sanitizarNombreArchivo(codigo_tarea.tarea)}.pdf`;
        generarPDFArbol(
            nodoRaiz,
            'Árbol completo de carpetas y archivos',
            obtenerSubtitulosContextoArbol(),
            nombreArchivo
        );
    };

    const handleExportarArbolCarpeta = (nodo) => {
        const nombreCarpeta = nodo.viewName || nodo.name || 'Carpeta';
        const nombreArchivo = `Arbol_${sanitizarNombreArchivo(nombreCarpeta)}.pdf`;
        generarPDFArbol(
            nodo,
            `Árbol de la carpeta: ${nombreCarpeta}`,
            obtenerSubtitulosContextoArbol(),
            nombreArchivo
        );
    };

    const handleUploadMultipleFiles = async () => {
        if (multipleFileList.length === 0) {
            message.error('Por favor, seleccione al menos un archivo.');
            return;
        }

        // Mostrar el spinner antes de empezar
        setUploadingMultiple(true);
        setUploadProgresAll(0);

        try {
            const totalFiles = multipleFileList.length;
            let completedFiles = 0;

            // Subir archivos en lotes pequeños para evitar bloqueo visual
            const chunkSize = 5; // 🔹 cantidad de archivos simultáneos
            for (let i = 0; i < totalFiles; i += chunkSize) {
                const chunk = multipleFileList.slice(i, i + chunkSize);

                // Ejecutar un grupo de subidas simultáneas
                const results = await Promise.allSettled(
                    chunk.map((file) =>
                        uploadSingleFile(file).then(() => {
                            completedFiles++;
                            setUploadProgresAll(Math.round((completedFiles / totalFiles) * 100));
                        })
                    )
                );

                // Esperar un pequeño tiempo para actualizar el spinner visualmente
                await new Promise((r) => setTimeout(r, 100));
            }

            message.success(`Subida completada: ${totalFiles} archivos.`);
            setModalMultipleVisible(false);
            setMultipleFileList([]);
        } catch (error) {
            console.error('Error al subir archivos:', error);
            message.error('Error al subir los archivos.');
        } finally {
            setUploadingMultiple(false);
            setUploadProgresAll(0);
        }
    };

    const uploadSingleFile = async (file) => {
        return new Promise(async (resolve, reject) => {
            try {
                const resourceType = 'Soporte Múltiple'; // O puedes definir otro tipo por defecto

                const pp = codigo_tarea.proyecto;
                const codigActividad = '/actividad_' + quitarPuntos(codigo_tarea.codActividad);
                const tarea = '/tarea_' + quitarPuntos(codigo_tarea.codTarea);
                const indicad = '/indicador_' + codigo_tarea.codIndicador;
                const mvcod = '/medio_verificacion_' + codigo_tarea.codMV;
                const nodeRaiz = 'DocumentosPOA/PlanOperativo';
                const codActividadParametro = pp + codigActividad + tarea + indicad + mvcod;

                const tmpRuta = nodeRaiz + '/' + codActividadParametro + '/' + selectedItem.pathFromClient;

                const resourceInfo = await handleAction({
                    user: userLogin || "admin",
                    actionType: 'uploadFile',
                    nameServer: file.name,
                    resourceDetails: {
                        nameResource: file.name,
                        Path: `${codActividadParametro}/${selectedItem.pathFromClient}`,
                        fatherResource: selectedItem.name,
                        typeResource: 'file',
                        key: `${selectedItem.pathFromClient}/${file.name}`,
                        fullPath: `${tmpRuta}/${file.name}`,
                        resourceType: resourceType
                    }
                });

                const formData = new FormData();
                formData.append('pathFromClient', tmpRuta);
                formData.append('file', file);
                formData.append('resourceInfo', JSON.stringify(resourceInfo));

                const response = await axios.post(`${baseUrl}/api/upload`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                const data = response.data;

                if (response.status === 200 && data.status === 'success') {
                    // Actualizar el árbol de directorios
                    const uid = generateRandomUid();
                    const newKey = `${selectedItem.key} - ${uid}`;

                    const newFileNode = {
                        name: file.name,
                        key: newKey,
                        type: 'file',
                        level: selectedItem.level + 1,
                        path: `${selectedItem.pathFromClient}/${data.serverFileName}`,
                        pathFromClient: selectedItem.pathFromClient,
                        uid: uid,
                        serverFileName: data.serverFileName,
                        isLeaf: true,
                        viewName: data.viewName,
                        typeS: 'Soporte',
                        resourceType: resourceType
                    };

                    const now = new Date();
                    const isoString = now.toISOString();

                    const newFileNodeDataFile = {
                        nameResource: file.name,
                        fullPath: `${nodeRaiz}/${codActividadParametro}/${selectedItem.pathFromClient}/${data.serverFileName}`,
                        user: userLogin,
                        state: 1,
                        typeAction: "uploadFile",
                        dateTime: `${isoString}`,
                        detailJSON: JSON.stringify(resourceInfo)
                    };

                    // Actualizar estados
                    setTreeData((prevTreeData) => addFileToTree(prevTreeData, selectedItem.pathFromClient, newFileNode));
                    setDataFile((prevDataFile) => [...prevDataFile, newFileNodeDataFile]);

                    resolve({ file: file.name, success: true });
                } else {
                    reject(new Error(`Error en la carga de ${file.name}`));
                }
            } catch (error) {
                reject(error);
            }
        });
    };

    return (
        <Layout>
            {showWarning && (
                <Alert
                    message="Advertencia"
                    description="Debe seleccionar un código de tarea para ver los medios de verificación."
                    type="warning"
                    showIcon
                    className="mb-3"
                />

            )}
            {loading ? (
                <div className="text-center">
                    <Spin size="large" />
                </div>
            ) : (
                <div className="container-fluid py-0 my-2" style={{ marginBottom: '10px !important' }}>
                    <Descriptions bordered column={1} style={{ padding: '0 0 0 10px !important' }}>
                        <Descriptions.Item label="Actividad">
                            {codigo_tarea.codActividad} - {codigo_tarea.actividad}
                        </Descriptions.Item>
                        <Descriptions.Item label="Tarea">
                            {codigo_tarea.tarea}
                        </Descriptions.Item>
                        <Descriptions.Item label="Indicador">
                            {codigo_tarea.indicador}
                        </Descriptions.Item>
                        <Descriptions.Item label="Medio de verificación">
                            {codigo_tarea.medioVerificacion}
                        </Descriptions.Item>
                    </Descriptions>
                    <Layout>
                        <Sider width={300} className="site-layout-background">
                            <div style={{ marginBottom: 8 }}>
                                <Input
                                    placeholder="Buscar"
                                    value={searchValue}
                                    onChange={onChange}
                                    style={{ width: 300 }}
                                    suffix={
                                        <>
                                            <Tooltip title="Buscar archivos por nombre" placement="top">
                                                <Button
                                                    type="primary"
                                                    icon={<SearchOutlined />}
                                                    onClick={onClickBuscar}
                                                    style={{ marginRight: 8 }}
                                                />
                                            </Tooltip>
                                            <Tooltip title="Restablecer filtros de carpetas" placement="top">
                                                <Button
                                                    icon={<ReloadOutlined />}
                                                    onClick={onLimpiarBusqueda}
                                                />
                                            </Tooltip>
                                            <Tooltip title="Filtrar carpetas sin archivos" placement="top">
                                                <Button
                                                    icon={<FileExclamationOutlined />}
                                                    onClick={handleFilterEmptyFolders}
                                                    style={{
                                                        backgroundColor: activeButton === 'emptyFolders' ? 'lightblue' : 'initial',
                                                        borderColor: activeButton === 'emptyFolders' ? 'blue' : 'initial',
                                                    }}
                                                />
                                            </Tooltip>
                                            <Tooltip title="Filtrar carpetas con archivos" placement="top">
                                                <Button
                                                    icon={<FileZipOutlined />}
                                                    onClick={handleFilterFolders}
                                                    style={{
                                                        backgroundColor: activeButton === 'folders' ? 'lightblue' : 'initial',
                                                        borderColor: activeButton === 'folders' ? 'blue' : 'initial',
                                                    }}
                                                />
                                            </Tooltip>
                                        </>
                                    }
                                />

                            </div>
                            <div style={treeContainerStyle}>

                                <DirectoryTree
                                    treeData={aplicarFiltroCoincidenciasEnArbol(treeData, filtroCoincidencias)}
                                    expandedKeys={expandedKeys}
                                    onExpand={onExpand}
                                    onSelect={handleSelect}
                                    autoExpandParent={autoExpandParent}
                                    titleRender={titleRenderer}
                                    showLine={true}
                                    multiple
                                    defaultExpandAll={true}
                                />
                            </div>
                        </Sider>
                        <Layout style={{ padding: '0px 24px 24px' }}>
                            <Content
                                className="site-layout-background"
                                style={{
                                    padding: 0,
                                    margin: 0,
                                    minHeight: 280,
                                }}
                            >

                                {selectedItem && !rootNodeSelected && selectedItem.type === 'file' && (
                                    <div style={{ textAlign: 'center', padding: '20px' }}>
                                        <WarningOutlined style={{ color: 'red', marginRight: '8px', fontSize: '2rem' }} />
                                        <h4>Seleccione el padre o la carpeta principal</h4>
                                    </div>
                                )}


                                {rootNodeSelected && !selectedItem && (
                                    <div style={{ textAlign: 'center', padding: '0px', marginTop: '-5px !important' }}>
                                        <p className="titulo_proyecto display-4">Bienvenid@</p>

                                        {treeData.length > 0 ? (
                                            <div>

                                                <Table
                                                    dataSource={treeData}
                                                    pagination={tableParams.pagination}
                                                    onChange={handleTableChange}
                                                    defaultExpandAllRows={true}
                                                    scroll={{
                                                        y: 340,
                                                    }}
                                                    columns={[
                                                        {
                                                            title: 'Resumen',
                                                            dataIndex: 'name',
                                                            key: 'key',
                                                            width: 260,

                                                            render: (text, record) => {
                                                                const maxLength = 50;
                                                                const maxLengthTooltip = 200;
                                                                let truncatedTitle;

                                                                if (record.type === 'file') {
                                                                    truncatedTitle = processFileName(record.viewName, maxLength);
                                                                } else {
                                                                    truncatedTitle = record.viewName;
                                                                }
                                                                const truncatedTooltip = processFileName(record.viewName, maxLengthTooltip);

                                                                return (
                                                                    <Tooltip title={truncatedTooltip} placement="right">
                                                                        <span>
                                                                            {record.type === 'folder' ? <FolderOutlined /> : <FileOutlined />}
                                                                            {` ${truncatedTitle}`}
                                                                        </span>
                                                                    </Tooltip>
                                                                );
                                                            }
                                                        },
                                                        {
                                                            title: 'Tipo',
                                                            key: 'extension',
                                                            width: 100,
                                                            align: 'center',
                                                            render: (text, record) => {
                                                                // Verifica si record.name es una cadena de texto
                                                                if (typeof record.name === 'string') {
                                                                    if (record.type === 'folder' && typeof record.fileCount !== 'undefined') {
                                                                        const pathContainsTarea = record.path.includes("tarea_221/indicador_34/");
                                                                        const fileCountToDisplay = pathContainsTarea ? (record.fileCount - record.filePhoto === 0 ? record.fileCount : record.fileCount - record.filePhoto) : record.fileCount;
                                                                        return (
                                                                            <>
                                                                                <Tooltip title="Cantidad de archivos que contiene la carpeta">
                                                                                    <span>Folder</span> <span></span>
                                                                                    <Badge
                                                                                        className="site-badge-count-109"
                                                                                        count={fileCountToDisplay}
                                                                                        overflowCount={9999}
                                                                                        style={{
                                                                                            backgroundColor: '#52c41a',
                                                                                        }}
                                                                                    />
                                                                                </Tooltip>
                                                                            </>
                                                                        );
                                                                    } else if (record.type === 'folder' && record.children.some(child => child.type === 'file')) {
                                                                        return (
                                                                            <>
                                                                                <Tooltip title="Cantidad de archivos que contiene la carpeta">
                                                                                    <span>Folder</span> <span>  </span>
                                                                                    <Badge
                                                                                        className="site-badge-count-109"
                                                                                        count={record.children.length}
                                                                                        style={{
                                                                                            backgroundColor: '#52c41a',
                                                                                        }}
                                                                                    />
                                                                                </Tooltip>
                                                                            </>
                                                                        );
                                                                    } else {
                                                                        if (record.type === 'folder') {
                                                                            return 'Folder'; // O cualquier otro mensaje adecuado para carpetas vacías
                                                                        } else {
                                                                            const fileNameParts = record.name.split('.');
                                                                            return fileNameParts.length > 1 ? fileNameParts.pop() : 'Archivo';
                                                                        }
                                                                    }
                                                                } else {
                                                                    // Maneja el caso en que record.name no es una cadena de texto
                                                                    return 'Desconocido'; // O cualquier otro valor predeterminado adecuado
                                                                }
                                                            },

                                                        },

                                                    ]}

                                                />
                                            </div>

                                        ) : (
                                            <>
                                                <h4 className='text-center mb-2'>Seleccione alguna opción del menu para navegar por los archivos</h4>
                                            </>)
                                        }

                                    </div>
                                )}
                                {selectedItem && !rootNodeSelected && selectedItem.type !== 'file' && (
                                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                                        <Row gutter={0}>
                                            <Col span={24}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>

                                                    <div style={{ flex: '1 1 auto' }}>
                                                        {isEditing ? (
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                <Input
                                                                    value={newName}
                                                                    onChange={handleEditNameChange}
                                                                    onBlur={() => {
                                                                        if (newName.trim() !== '') {
                                                                            handleEditFolder(selectedItem.path, newName.trim());
                                                                            toggleEditMode();
                                                                        } else {
                                                                            message.error('El nombre no puede estar vacío.');
                                                                            cancelEditMode();
                                                                        }
                                                                    }}
                                                                    onPressEnter={() => {
                                                                        if (newName.trim() !== '') {
                                                                            handleEditFolder(selectedItem.path, newName.trim());
                                                                            toggleEditMode();
                                                                        } else {
                                                                            message.error('El nombre no puede estar vacío.');
                                                                            cancelEditMode();
                                                                        }
                                                                    }}
                                                                    style={{ width: '85%' }}
                                                                />
                                                                <Button
                                                                    type="link"
                                                                    icon={<SaveOutlined />}
                                                                    onClick={() => {
                                                                        if (newName.trim() !== '') {
                                                                            handleEditFolder(selectedItem.path, newName.trim());
                                                                            toggleEditMode();
                                                                        } else {
                                                                            message.error('El nombre no puede estar vacío.');
                                                                            cancelEditMode();
                                                                        }
                                                                    }}
                                                                    className='btn btn-primary py-0'
                                                                >
                                                                    Guardar
                                                                </Button>
                                                                <Button
                                                                    type="link"
                                                                    icon={<CloseOutlined />}
                                                                    onClick={cancelEditMode}
                                                                    className='btn btn-danger py-0'
                                                                >
                                                                    Cancelar
                                                                </Button>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                {formattedTypeS !== 'Desconocido' ? (
                                                                    <strong>{formattedTypeS}: </strong>
                                                                ) : (
                                                                    <strong>Folder: </strong>
                                                                )}
                                                                <strong>{selectedItem.viewName}</strong>
                                                            </>
                                                        )}
                                                    </div>
                                                    {!isEditing && (
                                                        <div style={{ display: 'inline-flex', justifyContent: 'flex-end', gap: '6px' }}>
                                                            {mostrarFiltroCoincidencias && permissionAccess8 && (
                                                                <div style={{ flex: '1 1 auto' }}>
                                                                    <Tooltip title="Filtra los soportes de esta carpeta según si el mismo nombre/cédula aparece más de una vez">
                                                                        <Select
                                                                            value={filtroCoincidencias}
                                                                            onChange={setFiltroCoincidencias}
                                                                            style={{ width: 220 }}
                                                                            options={[
                                                                                { label: 'Todos', value: 'todos' },
                                                                                { label: 'Con coincidencias', value: 'repetidos' },
                                                                                { label: 'Sin coincidencias', value: 'unicos' },
                                                                            ]}
                                                                        />
                                                                    </Tooltip>
                                                                </div>
                                                            )}
                                                            {habilitarExportarArbol && (
                                                                <div style={{ marginBottom: 12, textAlign: 'center' }}>
                                                                    <Tooltip title="Exporta a PDF el árbol completo de carpetas y archivos de este proyecto">
                                                                        <Button
                                                                            type="primary"
                                                                            icon={<ApartmentOutlined />}
                                                                            onClick={handleExportarArbolCompleto}
                                                                        >
                                                                        </Button>
                                                                    </Tooltip>
                                                                </div>
                                                            )}
                                                            {permissionUpdate && selectedItem.typeS !== 'medio_verificacion' && (
                                                                <Tooltip title="Editar nombre de la carpeta">
                                                                    <Button
                                                                        type="link"
                                                                        icon={<EditOutlined />}
                                                                        onClick={toggleEditMode}
                                                                        className='btn btn-primary py-0'
                                                                    >

                                                                    </Button>
                                                                </Tooltip>
                                                            )}
                                                            {permissionPrint && (
                                                                <Tooltip title="Crear un directorio en la ubicación actual.">
                                                                    <Popconfirm
                                                                        title='Crear directorio'
                                                                        description="¿Segur@ que quieres crear un directorio?"
                                                                        onConfirm={showCreateFolderModal}
                                                                        okText="Sí"
                                                                        cancelText="No"
                                                                        icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                                                                    >
                                                                        <Button
                                                                            type="link"
                                                                            danger
                                                                            icon={<FolderAddFilled />}
                                                                            className='btn btn-primary py-0'
                                                                            style={{ color: '#FFF' }}
                                                                        />
                                                                    </Popconfirm>
                                                                </Tooltip>
                                                            )}
                                                            {permissionDelete && selectedItem.typeS !== 'medio_verificacion' && (
                                                                <Tooltip title="Eliminar este directorio y todo su contenido.">
                                                                    <Popconfirm
                                                                        title='Eliminación de directorio'
                                                                        description="¿Segur@ que quieres eliminar este directorio, no podrás revertir esta operación?"
                                                                        onConfirm={() => handleDeleteFolder(selectedItem.path, selectedItem)}
                                                                        okText="Sí"
                                                                        cancelText="No"
                                                                        icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                                                                    >
                                                                        <Button
                                                                            type="link"
                                                                            danger
                                                                            icon={<DeleteOutlined />}
                                                                            className='btn btn-primary py-0'
                                                                            style={{ color: '#FFF', background: '#ee1a0b', borderColor: '#ee1a0b' }}
                                                                        />
                                                                    </Popconfirm>
                                                                </Tooltip>
                                                            )}
                                                            {permissionExport && (
                                                                <Tooltip title="Descarga soportes">
                                                                    <Popconfirm
                                                                        title='Descargar soporte'
                                                                        description="¿Segur@ que quieres descargar el soporte?"
                                                                        onConfirm={() => handleDescargar(selectedItem)}
                                                                        okText="Sí"
                                                                        cancelText="No"
                                                                        okButtonProps={{
                                                                            loading: loading,
                                                                        }}
                                                                    >
                                                                        <Button
                                                                            type="link"
                                                                            icon={<DownloadOutlined />}
                                                                            className='btn btn-primary py-0'
                                                                        />
                                                                    </Popconfirm>
                                                                </Tooltip>
                                                            )}
                                                            {permissionInsert && (
                                                                <Tooltip title="Subir múltiples archivos">
                                                                    <Button
                                                                        type="link"
                                                                        icon={<UploadOutlined style={{ fontSize: '1.1rem' }} />}
                                                                        onClick={() => setModalMultipleVisible(true)}
                                                                        className='btn btn-primary py-0'
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>
                                        <Row gutter={1}>
                                            <Col span={20}>
                                                <Space direction="vertical" size="large" style={{ width: '100%', paddingTop: '0px !important', marginTop: '0px !important' }}>
                                                    {renderSelectedFiles()}
                                                </Space>
                                            </Col>
                                            <Col span={4}>

                                                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                                    {selectedItem.type === 'folder' && permissionInsert &&
                                                        selectedItem.children?.every(child => child.type !== 'folder') ? (
                                                        <Button
                                                            onClick={handleOkFile}
                                                            type="primary"
                                                            style={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                height: '150px',
                                                                width: '150px',
                                                                margin: '15px 0px 0px 10px',
                                                                border: '2px dashed #1890ff',
                                                                background: hover ? '#e6f7ff' : '#f5f5f5', // Cambio de color en hover
                                                                textAlign: 'center',
                                                                padding: '10px',
                                                                overflow: 'hidden',
                                                                whiteSpace: 'normal',
                                                                lineHeight: '1.2',
                                                                cursor: 'pointer',
                                                                transition: 'background 0.3s ease' // Transición suave al cambiar de color
                                                            }}
                                                            onMouseEnter={() => setHover(true)}
                                                            onMouseLeave={() => setHover(false)}
                                                        >
                                                            <CloudUploadOutlined style={{ fontSize: '40px', marginBottom: '10px', color: '#1677ff' }} />
                                                            <p style={{ fontSize: '12px', color: '#888', margin: 0 }}>
                                                                Haga clic para cargar el soporte.
                                                            </p>
                                                        </Button>

                                                    ) : null}
                                                </Space>
                                            </Col>
                                        </Row>
                                    </Space>
                                )}

                            </Content>
                        </Layout>
                    </Layout>
                    <Modal
                        title="Crear nueva carpeta"
                        open={isModalVisible}
                        onOk={handleCreateFolder}
                        onCancel={() => {
                            setIsModalVisible(false);
                            setNewFolderName(''); // Limpiar el nombre de la carpeta
                        }}
                        okText="Crear"
                        cancelText="Cancelar"
                    >
                        <Input
                            ref={inputRef} // Asigna la referencia al campo de entrada
                            placeholder="Nombre de la nueva carpeta"
                            value={newFolderName}
                            onChange={handleNameChange} // Cambia el valor usando la función handleNameChange
                            onKeyDown={handleKeyDown} // Maneja la tecla Enter
                        />
                    </Modal>
                    <Modal
                        title={<span style={{ fontWeight: 'bold', fontSize: '20px', textAlign: 'center', display: 'block' }}>Cargar soportes</span>}
                        open={isModalFileVisible}
                        onOk={handleOkFile}
                        onCancel={handleCancelFile}
                        width="45vw"
                        style={{
                            maxHeight: '60vh',
                            top: 20, // Ajusta este valor según lo necesario para mover el modal más arriba
                        }}
                        footer={null}
                        styles={{ paddingTop: '20px' }} // Opcional: para ajustar el padding superior del contenido del modal
                    >
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                                {permissionInsert && selectedItem?.pathFromClient && (
                                    <div>
                                        <p style={{ textAlign: 'center', marginBottom: '20px', color: '#5c5a5a' }}>Los documentos que se carguen deben corresponder con versiones finales del medio de verificación en formato PDF o Excel.</p>
                                        {versionFrom === '13' ? (<>
                                            <h4>{selectedItem.name}</h4>
                                            <div className='mt-0'>
                                                {getDirectoryDescription() && (
                                                    <Alert
                                                        banner
                                                        message={

                                                            <>  {getDirectoryDescription()}</>

                                                        }
                                                    />
                                                )}
                                            </div>
                                            <Form
                                                labelCol={{ span: 10 }}
                                                wrapperCol={{ span: 50 }}
                                                layout="vertical"
                                                form={form}
                                                initialValues={{
                                                    fecha: null,
                                                    titulo: selectedItem.name,
                                                    resourceType: null,
                                                }}
                                                onFinish={handleFormSubmit}
                                            >
                                                <Form.Item
                                                    label="Nombre del soporte"
                                                    name="titulo"
                                                >
                                                    <Input value={selectedItem.name} disabled /> {/* Deshabilita el campo para que no pueda ser editado */}
                                                </Form.Item>
                                                <Form.Item
                                                    label="Tipo de soporte"
                                                    name="resourceType"
                                                    rules={[{ required: true, message: 'Por favor, seleccione un tipo de soporte' }]}
                                                >
                                                    <Radio.Group>
                                                        <Radio value="Memoria">Memoria</Radio>
                                                        <Radio value="Acta">Acta</Radio>
                                                        <Radio value="PPT">PPT</Radio>
                                                        <Radio value="Asistencia">Asistencia</Radio>
                                                        <Radio value="Informe">Informe</Radio>
                                                        <Radio value="Foto">Foto</Radio>
                                                        <Radio value="Grabación">Grabación</Radio>
                                                        <Radio value="Correo Aprobación">Correo Aprobación</Radio>
                                                        <Radio value="Documento técnico">Documento técnico</Radio>
                                                        <Radio value="Otro">Otro</Radio>
                                                    </Radio.Group>
                                                </Form.Item>
                                                <Form.Item
                                                    label="Carga de Soporte"
                                                    name="soporte"
                                                    rules={[{ required: true, message: 'Por favor, seleccione un soporte' }]}
                                                >
                                                    <div className="centered-dragger">
                                                        <Tooltip title="Subir Soporte">
                                                            <Dragger
                                                                {...getUploadProps(selectedItem.pathFromClient)}
                                                                customRequest={handleCustomRequest} // Usa handleCustomRequest para la carga manual
                                                                onChange={({ fileList }) => setFileList(fileList)}
                                                                style={{ width: '300px' }} // Ajusta el ancho según tus necesidades
                                                            >
                                                                <p className="ant-upload-drag-icon">
                                                                    <InboxOutlined />
                                                                </p>
                                                                <p className="ant-upload-text">Haga clic para cargar el archivo.</p>
                                                                <p className="ant-upload-hint">Soporte para un archivo.</p>
                                                            </Dragger>
                                                        </Tooltip>
                                                    </div>
                                                </Form.Item>
                                                <Form.Item>
                                                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                                                        <Button
                                                            type="primary "
                                                            onClick={handleFormSubmit}
                                                            style={{ marginRight: 8 }}
                                                            disabled={isSubmitting}
                                                        >
                                                            {isSubmitting ? 'Guardando...' : 'Guardar'}
                                                        </Button>
                                                        <Button
                                                            type="primary"
                                                            onClick={handleCancelFile}
                                                            danger
                                                        >
                                                            Cancelar
                                                        </Button>
                                                    </div>
                                                </Form.Item>
                                            </Form>
                                        </>
                                        ) : (

                                            <Form
                                                labelCol={{ span: 10 }}
                                                wrapperCol={{ span: 50 }}
                                                layout="vertical"
                                                form={form}
                                                initialValues={{
                                                    fecha: null,
                                                    titulo: '',
                                                    resourceType: null,
                                                }}
                                                onFinish={handleFormSubmit}
                                            >
                                                <Form.Item
                                                    label="Nombre del soporte"
                                                    name="titulo"
                                                    rules={[
                                                        { required: true, message: 'Por favor, ingrese el Nombre del soporte' },
                                                        {
                                                            pattern: /^[0-9]{8}_[A-ZÁÉÍÓÚÑ][a-zA-Z0-9 _\-áéíóúÁÉÍÓÚñÑ.,]*_[A-ZÁÉÍÓÚÑ][a-zA-Z0-9 _\-áéíóúÁÉÍÓÚñÑ.,]*$/,
                                                            message: (
                                                                <div>
                                                                    <p>El formato debe ser ddmmyyyy_Tipo_Ubicación. Ej: 18112023_Memoria_ElCharco</p>
                                                                    <ul>
                                                                        <li>Fecha en la que se realizó el espacio o el documento, en formato día-mes-año</li>
                                                                        <li>Guion bajo</li>
                                                                        <li>Nombre descriptivo corto del archivo</li>
                                                                        <li>Guion bajo</li>
                                                                        <li>Especificar el municipio o vereda en la que se realizó el espacio</li>
                                                                    </ul>
                                                                </div>
                                                            ),
                                                        },
                                                    ]}
                                                >
                                                    <Input />
                                                </Form.Item>
                                                <Form.Item
                                                    label="Tipo de soporte"
                                                    name="resourceType"
                                                    rules={[{ required: true, message: 'Por favor, seleccione un tipo de soporte' }]}
                                                >
                                                    <Radio.Group>
                                                        <Radio value="Memoria">Memoria</Radio>
                                                        <Radio value="Acta">Acta</Radio>
                                                        <Radio value="PPT">PPT</Radio>
                                                        <Radio value="Asistencia">Asistencia</Radio>
                                                        <Radio value="Informe">Informe</Radio>
                                                        <Radio value="Foto">Foto</Radio>
                                                        <Radio value="Grabación">Grabación</Radio>
                                                        <Radio value="Correo Aprobación">Correo Aprobación</Radio>
                                                        <Radio value="Documento técnico">Documento técnico</Radio>
                                                        <Radio value="Otro">Otro</Radio>
                                                    </Radio.Group>
                                                </Form.Item>
                                                <Form.Item
                                                    label="Carga de Soporte"
                                                    name="soporte"
                                                    rules={[{ required: true, message: 'Por favor, seleccione un soporte' }]}
                                                >
                                                    <div className="centered-dragger">
                                                        <Tooltip title="Subir Soporte">
                                                            <Dragger
                                                                {...getUploadProps(selectedItem.pathFromClient)}
                                                                customRequest={handleCustomRequest} // Usa handleCustomRequest para la carga manual
                                                                onChange={({ fileList }) => setFileList(fileList)}
                                                                style={{ width: '300px' }} // Ajusta el ancho según tus necesidades
                                                            >
                                                                <p className="ant-upload-drag-icon">
                                                                    <InboxOutlined />
                                                                </p>
                                                                <p className="ant-upload-text">Haga clic para cargar el archivo.</p>
                                                                <p className="ant-upload-hint">Soporte para un archivo.</p>
                                                            </Dragger>
                                                        </Tooltip>
                                                    </div>
                                                </Form.Item>
                                                <Form.Item>
                                                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                                                        <Button
                                                            type="primary "
                                                            onClick={handleFormSubmit}
                                                            style={{ marginRight: 8 }}
                                                            disabled={isSubmitting}
                                                        >
                                                            {isSubmitting ? 'Guardando...' : 'Guardar'}
                                                        </Button>
                                                        <Button
                                                            type="primary"
                                                            onClick={handleCancelFile}
                                                            danger
                                                        >
                                                            Cancelar
                                                        </Button>
                                                    </div>
                                                </Form.Item>
                                            </Form>
                                        )}
                                    </div>
                                )}
                            </div>
                            {loading && (
                                <div style={{
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 1000
                                }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <Spin size="large" />
                                        <Progress percent={uploadProgress} style={{ marginTop: 16 }} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </Modal>

                    <Modal
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <UploadOutlined />
                                <span>Subir múltiples archivos</span>
                            </div>
                        }
                        open={modalMultipleVisible}
                        onCancel={() => {
                            setModalMultipleVisible(false);
                            setMultipleFileList([]);
                        }}
                        footer={[
                            <Button
                                key="cancel"
                                onClick={() => {
                                    setModalMultipleVisible(false);
                                    setMultipleFileList([]);
                                }}
                                disabled={uploadingMultiple}
                            >
                                Cancelar
                            </Button>,
                            <Button
                                key="upload"
                                type="primary"
                                loading={uploadingMultiple}
                                onClick={handleUploadMultipleFiles}
                                disabled={multipleFileList.length === 0 || uploadingMultiple}
                                icon={<UploadOutlined />}
                            >
                                {uploadingMultiple
                                    ? `Subiendo... (${uploadProgress || 0}%)`
                                    : `Subir ${multipleFileList.length} Archivo${multipleFileList.length !== 1 ? 's' : ''}`}
                            </Button>,
                        ]}
                        width={800}
                        style={{ maxWidth: '90vw' }}
                    >
                        {/** 👇 Nuevo estado spinner para carga local */}
                        <Spin
                            spinning={uploadingMultiple || loadingFiles}
                            tip={
                                uploadingMultiple
                                    ? `Subiendo archivos... ${uploadProgresAll || 0}%`
                                    : 'Cargando archivos... espere un momento'
                            }
                            size="large"
                            style={{ width: '100%' }}
                        >
                            <div style={{ padding: '0 16px' }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: 16,
                                        marginBottom: 16,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Statistic
                                        title="Archivos seleccionados"
                                        value={multipleFileList.length}
                                        prefix={<FileTextOutlined />}
                                        valueStyle={{ fontSize: '24px' }}
                                    />
                                    <Statistic
                                        title="Tamaño total"
                                        value={(
                                            multipleFileList.reduce((total, file) => total + (file.size || 0), 0) /
                                            (1024 * 1024)
                                        ).toFixed(2)}
                                        suffix="MB"
                                        valueStyle={{ fontSize: '24px' }}
                                    />
                                </div>

                                {/* Área de carga */}
                                <Card size="small" style={{ marginBottom: 16 }}>
                                    <Form layout="vertical">
                                        <Dragger
                                            multiple
                                            fileList={multipleFileList}
                                            beforeUpload={() => false} // evita upload automático

                                            // 👇 Aquí es donde agregamos la animación de carga local
                                            onChange={(info) => {
                                                setLoadingFiles(true); // muestra el spinner de carga local

                                                setTimeout(() => {
                                                    const newFiles = info.fileList
                                                        .map((f) => f.originFileObj)
                                                        .filter(Boolean);

                                                    const uniqueFiles = [];
                                                    const seen = new Set();

                                                    for (const f of newFiles) {
                                                        const sig = f.name + f.size;
                                                        if (!seen.has(sig)) {
                                                            seen.add(sig);
                                                            uniqueFiles.push(f);
                                                        }
                                                    }

                                                    const maxSize = 100 * 1024 * 1024;
                                                    const filtered = uniqueFiles.filter((f) => {
                                                        if (f.size === 0 || f.type?.includes('directory')) {
                                                            message.warning(`"${f.name}" es una carpeta.`);
                                                            return false;
                                                        }
                                                        if (f.size > maxSize) {
                                                            message.warning(`"${f.name}" excede el tamaño máximo de 100MB.`);
                                                            return false;
                                                        }
                                                        return true;
                                                    });

                                                    setMultipleFileList(filtered);
                                                    setLoadingFiles(false); // oculta el spinner
                                                }, 200); // pequeño delay para que se note el "cargando"
                                            }}

                                            onRemove={(file) => {
                                                const newList = multipleFileList.filter((f) => f.uid !== file.uid);
                                                setMultipleFileList(newList);
                                            }}
                                            showUploadList={false}
                                        >
                                            <div style={{ padding: '40px 20px' }}>
                                                <p className="ant-upload-drag-icon" style={{ marginBottom: 16 }}>
                                                    <InboxOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                                                </p>
                                                <p
                                                    className="ant-upload-text"
                                                    style={{
                                                        fontSize: '16px',
                                                        fontWeight: 500,
                                                        marginBottom: 8,
                                                    }}
                                                >
                                                    Haga clic o arrastre los archivos aquí
                                                </p>
                                                <p
                                                    className="ant-upload-hint"
                                                    style={{
                                                        fontSize: '14px',
                                                        color: '#666',
                                                    }}
                                                >
                                                    Soporte para múltiples archivos. No se permiten carpetas.
                                                </p>
                                                <Button
                                                    type="primary"
                                                    ghost
                                                    style={{ marginTop: 16 }}
                                                    icon={<FolderOpenOutlined />}
                                                >
                                                    Explorar archivos
                                                </Button>
                                            </div>
                                        </Dragger>
                                    </Form>
                                </Card>

                                {/* Lista de archivos con paginación */}
                                {multipleFileList.length > 0 && (
                                    <Card
                                        title={
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <PaperClipOutlined />
                                                <span>Archivos a subir ({multipleFileList.length})</span>
                                                <Button
                                                    type="link"
                                                    danger
                                                    size="small"
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => {
                                                        Modal.confirm({
                                                            title: '¿Eliminar todos los archivos?',
                                                            content: `Se eliminarán ${multipleFileList.length} archivos de la lista.`,
                                                            okText: 'Sí, eliminar',
                                                            cancelText: 'Cancelar',
                                                            okType: 'danger',
                                                            onOk: () => setMultipleFileList([]),
                                                        });
                                                    }}
                                                    disabled={uploadingMultiple}
                                                >
                                                    Limpiar todo
                                                </Button>
                                            </div>
                                        }
                                        size="small"
                                        style={{ marginBottom: 16 }}
                                    >
                                        <List
                                            size="small"
                                            dataSource={multipleFileList}
                                            pagination={{
                                                pageSize: 10,
                                                showSizeChanger: false, // 🔹 sin selector de tamaño
                                                showQuickJumper: false,
                                                size: 'small',
                                                responsive: true,
                                                style: { marginBottom: 0 },
                                            }}
                                            renderItem={(file, index) => (
                                                <List.Item
                                                    style={{
                                                        padding: '12px 16px',
                                                        borderBottom: '1px solid #f0f0f0',
                                                    }}
                                                    actions={[
                                                        <Tooltip title="Eliminar de la lista" key="delete">
                                                            <Button
                                                                type="text"
                                                                danger
                                                                size="small"
                                                                icon={<DeleteOutlined />}
                                                                onClick={() => {
                                                                    const newFileList = multipleFileList.filter((_, i) => i !== index);
                                                                    setMultipleFileList(newFileList);
                                                                }}
                                                                disabled={uploadingMultiple}
                                                            />
                                                        </Tooltip>,
                                                    ]}
                                                >
                                                    <List.Item.Meta
                                                        avatar={
                                                            <div
                                                                style={{
                                                                    width: 32,
                                                                    height: 32,
                                                                    borderRadius: 6,
                                                                    background: '#1890ff',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    color: 'white',
                                                                }}
                                                            >
                                                                <FileOutlined />
                                                            </div>
                                                        }
                                                        title={
                                                            <Tooltip title={file.name}>
                                                                <span
                                                                    style={{
                                                                        display: 'block',
                                                                        maxWidth: '300px',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    {file.name}
                                                                </span>
                                                            </Tooltip>
                                                        }
                                                        description={
                                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                                <div>Tamaño: {(file.size / (1024 * 1024)).toFixed(2)} MB</div>
                                                                <div>Tipo: {file.type || 'Desconocido'}</div>
                                                            </div>
                                                        }
                                                    />
                                                    {uploadingMultiple && (
                                                        <Progress percent={uploadProgress} size="small" style={{ width: 80 }} />
                                                    )}
                                                </List.Item>
                                            )}
                                        />
                                    </Card>
                                )}

                                {/* Información adicional */}
                                <Alert
                                    message="Información importante"
                                    description={
                                        <ul style={{ margin: 0, paddingLeft: 16 }}>
                                            <li>Seleccione solo archivos individuales, no carpetas</li>
                                            <li>Tamaño máximo por archivo: 100MB</li>
                                            <li>Puede seleccionar hasta 100 archivos a la vez</li>
                                            <li>Los archivos se subirán en paralelo para mayor velocidad</li>
                                        </ul>
                                    }
                                    type="info"
                                    showIcon
                                    style={{ marginTop: 16 }}
                                />
                            </div>
                        </Spin>
                    </Modal>

                </div >
            )}
        </Layout >
    );
};

export default FileManagerPP;
